import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthImg } from "@/components/AuthImg";
import { ScanField } from "@/components/ScanField";
import { RedTwinView } from "@/components/RedTwinView";
import { numberWords, PICK_ROLES, ROLE_WORDS } from "@/lib/numberWords";
import { HelpTip } from "@/components/HelpTip";
import { PhotoStage, PhotoControls, usePhotoGestures } from "@/components/PhotoStage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MeasurementsEditor } from "@/components/MeasurementsEditor";
import { AiSuggestedBadge } from "@/components/AiSuggestedBadge";
import { TagHeadingRename } from "@/components/TagHeadingRename";
import { TagNameChoice, TagStepBar } from "@/components/TagNaming";
import { ChoiceButton, CHOICE_CLASS } from "@/components/ChoiceButton";
import { copyText } from "@/lib/copyText";
import { labelledShape } from "@/lib/numberShapes";
import { AiNudgeLine, aiClassOf, classOfRole, classWords } from "@/components/AiNudge";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Camera, Copy, PlusCircle, CheckCircle2, X, ImageOff,
  ScanText, Tag, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, Maximize2, Download,
  Check, Pencil, ListPlus, Globe, AlertOctagon, MoveRight, Undo2,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";

const CONF_META = {
  high: "bg-emerald-50 text-emerald-700 border-emerald-200",
  med: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-red-50 text-red-700 border-red-200",
};
const CTX_KEYS = ["year", "make", "model", "engine", "category"];
// WHAT ELSE A VALUE ON A TAG CAN BE (his decision 1(b), 2026-10-13) — labelled numbers, not
// keywords. A rating, a date code and a quantity are findable as text but are never part numbers.
// HIS RULE, 2026-10-08: *"Default stays at 4. Selecting MORE than 4 brings up a prompt asking
// WHY... No artificial cap."* Past 4 the WHY is asked here and ENFORCED on the server; past
// BACKGROUND_ABOVE the read runs as a background batch job with progress, the same path the
// truck photo scans use — the crew is never stopped short.
const PHOTO_DEFAULT = 4;
const BACKGROUND_ABOVE = 20;
const PHOTO_REASONS = [
  { key: "assembly_many_tags", label: "Assembly with several tags" },
  { key: "hard_to_read", label: "Hard to read" },
  { key: "other", label: "Other" },
];

// NEVER PAY FOR THE SAME PHOTO TWICE. A 30-photo read that died partway through used to start
// again from photo 1 and charge for all 30 (his report, 2026-10-14). Finished batches are now
// marked done as they land, this says how many that is, and picking up where it stopped is ONE tap.
// "Re-read everything" stays available, deliberately, for when another look is genuinely wanted.
const ResumeStrip = ({ readState, list, selected, setSelected, rereadAll, setRereadAll, photoReason, setWhyOpen }) => {
  const rows = Object.entries(readState.tasks || {})
    .map(([task, t]) => ({ task, ...t, readCount: (t.read || []).length, unread: t.unread || [] }))
    .filter((r) => r.readCount > 0);
  if (!rows.length) return null;
  const pickUnread = (r) => {
    if (!r.unread.length) return;
    if (r.unread.length > PHOTO_DEFAULT && !photoReason) { setWhyOpen(true); return; }
    setSelected(new Set(r.unread));
  };
  return (
    <div data-testid="ai-resume-strip" className="rounded-xl border border-sky-200 bg-sky-50 p-3 space-y-2">
      {rows.map((r) => (
        <div key={r.task} data-testid={`ai-resume-${r.task}`} className="text-[12px] text-sky-900">
          <b>{r.label}</b> has already read {r.readCount} of these {list.length} photos.{" "}
          {r.unread.length
            ? <>They are skipped unless you tick below, so you are not charged twice.</>
            : <>Every photo has been read for this already.</>}
          {r.unread.length > 0 && (
            <ChoiceButton tone="sky" data-testid={`ai-resume-pick-${r.task}`} onClick={() => pickUnread(r)} className="ml-1">
              Pick the {r.unread.length} not read yet
            </ChoiceButton>
          )}
        </div>
      ))}
      <label title="Only tick this when you want photos that were already read paid for and read again — after better photos, for example. Left alone, a read that stopped halfway picks up where it stopped."
        className="flex items-center gap-2 text-[12px] font-semibold text-sky-900 cursor-pointer">
        <input type="checkbox" data-testid="ai-reread-all" checked={rereadAll}
          onChange={(e) => setRereadAll(e.target.checked)} className="w-4 h-4 accent-sky-700" />
        Re-read everything (charges for the photos already read)
      </label>
    </div>
  );
};

// Full-screen "AI workbench": ONE Identify call returns a suggestion for every listable field,
// shown inline at the field it fills with a one-click Use. Identify/describe only — no pricing/Perplexity.
// GUARDRAILS: OEM/serial INSERT-then-human-confirm (never auto-verified); interchange/markings = Copy only;
// read numbers may also go to free Keywords; descriptive + description stamped "AI Assist — unverified"
// (ai_unverified keeps blocking Finalize); keywords save free.
export function AiWorkbench({ partId, photos, context, tagSerial = "", oemNumber = "", partNumbers = [],
                             existingNumbers = [], measurements = [], onApplySuggestion, onMarketApply,
                             onCreateTaxonomy, onAddKeyword, onNumberAdded, onUndoSuggestion, onRemoveKeyword,
                             canCreateList = false }) {
  const { has } = useAuth();
  const canUse = has("use_ai_assist");
  const isAdmin = has("manage_ai_budget");

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [numBusy, setNumBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [numbers, setNumbers] = useState(null);
  const [applied, setApplied] = useState(() => new Set());
  const [insertedNums, setInsertedNums] = useState(() => new Set());
  const [addedKw, setAddedKw] = useState(() => new Set());
  const [addedToList, setAddedToList] = useState(() => new Set());
  // WHAT'S DONE, AND WHERE IT WENT (his instruction, 2026-10-17: *"on a part with a lot of numbers
  // it's hard to tell which ones I've already used. Every number I've placed should clearly look
  // DONE — greyed or ticked, saying where it went"*). value -> "the ECM's part number".
  const [placedAs, setPlacedAs] = useState({});
  // HOW it was placed, so UNDO knows what to take back out: a form field, a saved number, a word.
  const [placedHow, setPlacedHow] = useState({});
  const [undoBusy, setUndoBusy] = useState(null);
  const [confirmCreate, setConfirmCreate] = useState(null);   // adding to a SHARED list asks once
  const [skippedNums, setSkippedNums] = useState(() => new Set());   // "not needed" — deliberately settled
  const [reopened, setReopened] = useState(() => new Set());         // a done row asked to show its buttons again
  const [replaceAsk, setReplaceAsk] = useState(null);                // ONE OEM # AND ONE SERIAL — never two silently
  const [addNumOpen, setAddNumOpen] = useState(null);                // which group's "add a number" form is open
  const [addNumRole, setAddNumRole] = useState("service_part");
  const [addNumBusy, setAddNumBusy] = useState(false);
  const [numEdits, setNumEdits] = useState({});   // per-index edited OCR value
  const [addingNum, setAddingNum] = useState({});   // per-index "Add a #" busy flag
  const [editNum, setEditNum] = useState(() => new Set());   // per-index edit-then-use toggle
  const [groupMoves, setGroupMoves] = useState({});          // tag grouping: value -> the group a person moved it to
  const [extraGroups, setExtraGroups] = useState([]);        // groups a person made, one click each
  const [assemblyFlips, setAssemblyFlips] = useState(() => new Set());   // "this tag IS the assembly's own"
  const [layout, setLayout] = useState({ names: {}, kept_mine: [] });  // saved on the read: renamed headings, kept answers
  const [renameFor, setRenameFor] = useState(null);                  // which tag's heading is being renamed
  const [tagNames, setTagNames] = useState(null);                    // step 1: each tag's suggestion and whether a person named it
  const [nameBusy, setNameBusy] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(null);                  // the value whose Switch is running
  const [ownAdded, setOwnAdded] = useState({});                      // per tag: numbers typed in by hand this visit
  const [dragVal, setDragVal] = useState(null);              // PC: the number being dragged between groups
  const [promoting, setPromoting] = useState(null);          // the one value asking "really the assembly's?"
  const [twinFor, setTwinFor] = useState(null);              // red twin view: {value, row}
  const [closedGroups, setClosedGroups] = useState(null);    // null = the default for this many tags
  const [priceBusy, setPriceBusy] = useState(false);
  const [priceResult, setPriceResult] = useState(null);   // Tier 2 market lookup (Perplexity)
  const [needsCreate, setNeedsCreate] = useState({});
  const [restored, setRestored] = useState(false);
  const [active, setActive] = useState(0);
  const photoG = usePhotoGestures(active);
  // inline zoom/pan/rotate — mirrors the Identify screen PhotoWorkspace viewer
  const [fullIdx, setFullIdx] = useState(null);   // the one full-screen viewer, opened from here
  const [selected, setSelected] = useState(() => new Set());
  const [hint, setHint] = useState("");
  const [sentPreview, setSentPreview] = useState([]);   // server-assembled "what's sent to AI"
  // MORE THAN 4 PHOTOS — the WHY, and the progress of a big background read.
  const [whyOpen, setWhyOpen] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoReason, setPhotoReason] = useState("");
  const [photoNote, setPhotoNote] = useState("");
  const [bgRead, setBgRead] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);
  // WHOSE ANSWER IS THIS? The frozen-camera lesson, applied here: the progress poll and the
  // live ask both close over the part they started on, and this screen is reached on a route
  // where only the id changes (/sort-queue/:partId), so the component can be handed a DIFFERENT
  // part without ever unmounting. A finished read for part A must never be written onto part B.
  const [readState, setReadState] = useState(null);   // {total, tasks:{task:{read:[],unread:[]}}}
  const [rereadAll, setRereadAll] = useState(false);  // deliberate: pay to read them again
  const partIdRef = useRef(partId);
  useEffect(() => {
    partIdRef.current = partId;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    setBgRead(null); setBusy(false); setNumBusy(false);
  }, [partId]);

  const list = (photos || []).filter((p) => p && p.file_id && !p.is_archived);
  const numbersOnFile = (existingNumbers || []).filter(Boolean);

  // WHICH PHOTOS EACH TASK HAS ALREADY READ — so a read that stopped halfway can be picked up
  // without paying for the same photo twice (his instruction, 2026-10-14).
  const loadReadState = useCallback(() => {
    api.get(`/parts/${partId}/ai-research/photo-read-state`)
      .then(({ data }) => setReadState(data)).catch(() => setReadState(null));
  }, [partId]);

  const loadStatus = useCallback(() => {
    api.get("/ai/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);
  useEffect(() => { if (canUse) loadStatus(); }, [canUse, loadStatus]);
  // THE TAG LAYOUT LIVES ON THE READ (his call, 21 Oct): a renamed heading, the numbers moved between tags, the tags
  // somebody added and the answers kept against the AI come back after a reload, for whoever opens the part next.
  const layoutRid = numbers?.return_id || null;
  useEffect(() => {
    if (!canUse || !layoutRid) return;
    api.get(`/parts/${partId}/ai-research/${layoutRid}/layout`).then(({ data }) => {
      setGroupMoves(data.moves || {});
      setExtraGroups((data.extra_groups || []).map((g) => ({ name: "", belongs_to: g.belongs_to || "component", numbers: [], made_by_person: true })));
      setAssemblyFlips(new Set(data.flips || []));
      setLayout({ names: data.names || {}, kept_mine: data.kept_mine || [] });
    }).catch(() => setLayout({ names: {}, kept_mine: [] }));
  }, [canUse, partId, layoutRid]);
  const loadTagNames = useCallback(() => {
    if (!layoutRid) return;
    api.get(`/parts/${partId}/ai-research/${layoutRid}/tag-names`)
      .then(({ data }) => setTagNames(data)).catch(() => setTagNames(null));
  }, [partId, layoutRid]);
  useEffect(() => { if (canUse) loadTagNames(); }, [canUse, loadTagNames]);
  if (!canUse) return null;

  const available = !!status?.available;
  const paused = !!status?.paused;

  const launch = () => {
    loadStatus();
    loadReadState();
    setRereadAll(false);
    setApplied(new Set()); setInsertedNums(new Set()); setAddedKw(new Set()); setAddedToList(new Set()); setNumEdits({}); setAddingNum({}); setEditNum(new Set()); setNeedsCreate({});
    // a different part starts clean — a number placed on part A must never read DONE on part B
    setPlacedAs({}); setSkippedNums(new Set()); setReopened(new Set()); setOwnAdded({}); setLayout({ names: {}, kept_mine: [] }); setTagNames(null);
    setActive(0); photoG.reset(); setRestored(false); setResult(null); setNumbers(null); setHint("");
    // Verification integrity: nothing is auto-selected. The user must LOOK at and
    // actively pick which photos to hand the AI (human confirms what's sent).
    setSelected(new Set());
    setPhotoReason(""); setPhotoNote(""); setWhyOpen(false); setPendingPhoto(null); setBgRead(null);
    setOpen(true);
    // A read already running on this part (started here, or by somebody else) is picked back up
    // instead of being orphaned — the job lives on the server.
    api.get(`/parts/${partId}/ai-research/read-status`).then(({ data }) => {
      if (data.status === "running") {
        setBgRead(data);
        if (data.task === "read_numbers") { setNumBusy(true); followBigRead(setNumBusy, setNumbers); }
        else { setBusy(true); followBigRead(setBusy, setResult); }
      }
    }).catch(() => {});
    api.get(`/parts/${partId}/ai-context`).then(({ data }) => setSentPreview(data.sent || [])).catch(() => setSentPreview([]));
    api.get(`/parts/${partId}/ai-research`).then(({ data }) => {
      const all = data.events || [];
      const evs = all.filter((e) => e.kind === "return" || (e.numbers || []).length > 0);
      const lastDesc = evs.find((e) => e.describe && (e.describe.suggested_name || e.describe.description || e.describe.suggested_category || e.describe.suggested_part_type || e.describe.suggested_brand));
      const lastNums = evs.find((e) => (e.numbers || []).length > 0);
      if (lastDesc) { setResult({ describe: lastDesc.describe, return_id: lastDesc.id, abstained: lastDesc.abstained, abstain_reason: lastDesc.abstain_reason, at: lastDesc.at }); setRestored(true); }
      if (lastNums) { setNumbers({ numbers: lastNums.numbers, own_tags: lastNums.own_tags || [], tag_groups: lastNums.tag_groups || [], abstained: lastNums.abstained, return_id: lastNums.id }); }
      // "Not needed" survives a reload, so a finished group stays finished (newest decision wins).
      const sk = new Set();
      [...all].reverse().forEach((e) => {
        if (e.kind !== "decision") return;
        const ref = String(e.suggestion_ref || "");
        if (ref.startsWith("not_needed:")) sk.add(ref.slice("not_needed:".length));
        else if (ref.startsWith("needed_after_all:")) sk.delete(ref.slice("needed_after_all:".length));
      });
      if (sk.size) setSkippedNums(sk);
    }).catch(() => {});
  };

  const toggleSelect = (fileId) => {
    if (selected.has(fileId)) {
      setSelected((s) => { const n = new Set(s); n.delete(fileId); return n; });
      return;
    }
    // Crossing the default asks WHY once, with quick choices — not on every photo after it.
    if (selected.size >= PHOTO_DEFAULT && !photoReason) {
      setPendingPhoto(fileId);
      setWhyOpen(true);
      return;
    }
    setSelected((s) => new Set(s).add(fileId));
  };
  const confirmWhy = (key) => {
    if (key === "other" && !photoNote.trim()) { toast.error("Say why in a word or two."); return; }
    setPhotoReason(key);
    setWhyOpen(false);
    if (pendingPhoto) setSelected((s) => new Set(s).add(pendingPhoto));
    setPendingPhoto(null);
  };
  const sendIds = () => Array.from(selected);

  const ctx = () => ({
    ...(context || {}),
    tag_serial: tagSerial || undefined,
    existing_numbers: numbersOnFile.length ? numbersOnFile.join(", ") : undefined,
  });

  // A BIG READ RUNS ON THE SERVER. This follows its progress and picks up the finished answer,
  // so closing the tab or walking away never loses a read that was paid for.
  const followBigRead = (setLoading, setData) => {
    const owner = partId;                 // the part this read belongs to, for good
    const tick = async () => {
      if (partIdRef.current !== owner) { setLoading(false); return; }   // another part is on screen
      try {
        const { data } = await api.get(`/parts/${owner}/ai-research/read-status`);
        if (partIdRef.current !== owner) { setLoading(false); return; }
        setBgRead(data);
        if (data.status === "done" && data.result) {
          setData(data.result); setBgRead(null); setLoading(false); loadReadState(); return;
        }
        if (data.status === "error" || data.status === "stopped") {
          toast.error(data.message || "The read stopped before it finished.");
          // Whatever it DID read is kept and shown — a read cut short is not a wasted read.
          if (data.result) setData(data.result);
          setBgRead(null); setLoading(false); loadReadState(); return;
        }
      } catch { /* a blip on one poll is not a failed read — keep watching */ }
      pollRef.current = setTimeout(tick, 2500);
    };
    tick();
  };

  const ask = async (task, setLoading, setData, refine = "", freshRead = false) => {
    if (!selected.size) { toast.error("Select at least one photo to send."); return; }
    if (selected.size > PHOTO_DEFAULT && !photoReason) { setWhyOpen(true); return; }
    const owner = partId;                 // whose answer this is, checked again on arrival
    setLoading(true);
    try {
      const { data } = await api.post(`/parts/${owner}/ai-research/ask`, {
        task, photo_file_ids: sendIds(), context: ctx(), refine: (refine || "").trim() || undefined,
        photo_reason: photoReason || undefined,
        photo_reason_note: photoNote.trim() || undefined,
        reread_all: (rereadAll || freshRead) || undefined,
      });
      if (partIdRef.current !== owner) { setLoading(false); return; }   // a different part is on screen now
      if (data.status === "running") { setBgRead(data); followBigRead(setLoading, setData); return; }
      setData(data);
      loadReadState();
      if (data.photos_failed > 0) toast.warning(`${data.photos_read} of ${data.photos_sent} photos were read — ${data.photos_failed} could not be loaded.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI request failed.");
    }
    setLoading(false);
  };
  const runIdentify = () => { setRestored(false); setNeedsCreate({}); setPriceResult(null); setApplied(new Set()); return ask("describe", setBusy, setResult, hint); };
  // A NEW READ STARTS CLEAN. The per-row edits were keyed by POSITION, so after a re-read an
  // edit typed against row 2 of the old answer was shown — and could be inserted — against
  // whatever number happened to be row 2 of the new one.
  // READ NUMBERS, AND NOTHING ELSE. A re-run reads the NUMBERS again — it never re-identifies the
  // part — and it is deliberately a fresh read of the photos you picked, charged as one (his
  // instruction, 2026-10-14). That is the opposite of the resume rule, and on purpose: resume
  // exists so a read that STOPPED is not paid for twice; this button exists for when you want the
  // numbers read again. Nothing already on the part is touched — the answer only ever suggests.
  const runNumbers = (fresh = false) => {
    setNumEdits({}); setEditNum(new Set()); setInsertedNums(new Set()); setAddedToList(new Set()); setAddingNum({});
    setGroupMoves({}); setExtraGroups([]); setAssemblyFlips(new Set()); setClosedGroups(null); setOwnAdded({});
    setLayout({ names: {}, kept_mine: [] });
    return ask("read_numbers", setNumBusy, setNumbers, "", fresh);
  };

  // Tier 2 — live-web market value via Perplexity. Dormant until a research key exists.
  const marketReady = !!status?.search_ready && (status?.tasks ? status.tasks.find_pricing !== false : true);
  const runMarket = async () => {
    setPriceBusy(true);
    try {
      const { data } = await api.post(`/parts/${partId}/ai-research/ask`, { task: "find_pricing", photo_file_ids: [], context: ctx() });
      setPriceResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Market lookup failed.");
    }
    setPriceBusy(false);
  };

  const logDecision = (rid, label, value) => api.post(`/parts/${partId}/ai-research/decision`, {
    conclusion: "accepted", entered_value: `${label}: ${value}`, return_id: rid, note: "Applied AI suggestion",
  }).catch(() => {});

  // "Not needed" is a decision, not a mood — it is written down, so a finished group stays finished.
  const logSkip = (value, on) => api.post(`/parts/${partId}/ai-research/decision`, {
    conclusion: on ? "rejected" : "entered_instead",
    suggestion_ref: on ? `not_needed:${value}` : `needed_after_all:${value}`,
    entered_value: value, return_id: numbers?.return_id || null,
    note: on ? "Marked not needed on the read" : "Brought back after being marked not needed",
  }).catch(() => {});

  const use = (field, value, label) => {
    if (!value || !onApplySuggestion) return;
    const ok = onApplySuggestion(field, value);
    if (ok === false) {
      if ((field === "category" || field === "part_type" || field === "name" || field === "brand") && canCreateList) setNeedsCreate((s) => ({ ...s, [field]: value }));
      return;
    }
    setApplied((s) => new Set(s).add(field));
    toast.success(`${label} added to the form ✓`);
    logDecision(result?.return_id, label, value);
  };

  const createList = async (kind, value, label) => {
    if (!onCreateTaxonomy) return;
    const ok = await onCreateTaxonomy(kind, value);
    if (ok) {
      setApplied((s) => new Set(s).add(kind));
      setNeedsCreate((s) => { const n = { ...s }; delete n[kind]; return n; });
      logDecision(result?.return_id, `${label} (added to list)`, value);
    }
  };

  // Insert a read number into an identity field. The value goes in UNVERIFIED (it marks the field
  // AI-estimated, which blocks Finalize until a person confirms it) — and now the READ ITSELF
  // travels with it: confidence, the raw read and the unsure characters, so they are saved on the
  // part instead of living only in the AI log.
  const insertNumber = (targetField, value, label, n) => {
    if (!onApplySuggestion) return;
    onApplySuggestion(targetField, value, n ? {
      confidence: n.confidence || null,
      raw_read: n.raw_read || n.value || null,
      unsure_chars: n.ambiguous_chars || [],
      // The red "check the photo" stamp travels with the read and stays on the field until
      // somebody clicks ✓verify (his instruction, 2026-10-09).
      needs_retake: !!n.needs_retake,
      retake_reason: n.retake_reason || null,
      research_id: numbers?.return_id || null,
    } : null);
    setInsertedNums((s) => new Set(s).add(`${targetField}:${value}`));
    markPlaced(value, targetField === "oem_number" ? "the assembly's part number"
      : targetField === "tag_serial" ? "the assembly's serial number" : label,
      { kind: "field", field: targetField });
    logDecision(numbers?.return_id, label, value);
    toast.success(`Inserted into ${label} — confirm it against the tag`);
  };

  // Effective (possibly-edited) OCR value for a number row.
  const numVal = (i, original) => (numEdits[i] !== undefined ? numEdits[i] : original);

  // ====================================================== WHAT IS ALREADY DONE, AND WHERE IT WENT
  // Reads BOTH this session's clicks and what is already saved on the part — so a number placed
  // before a reload still looks done, which was the actual confusion on a 20-number engine.
  const sameVal = (a, b) => String(a || "").trim().toUpperCase() === String(b || "").trim().toUpperCase();
  // ONE WORDING, before and after a reload: "serial number on ECM PLATE"
  const whereWords = (role, label, primary) => {
    const roleW = ROLE_WORDS[role] || "number";
    if (!label) return `${roleW} on the assembly`;
    // Only the ONE primary may say "'s part number" — a demoted twin says it is kept, nothing more.
    if (primary) return `${label}'s part number`;
    if (role === "service_part") return `a spare on ${label} — not its chosen number`;
    return `${roleW} on ${label}`;
  };
  const savedPlacement = (val) => {
    if (oemNumber && sameVal(oemNumber, val)) return "the assembly's part number";
    if (tagSerial && sameVal(tagSerial, val)) return "the assembly's serial number";
    const hit = savedFor(val);
    if (!hit) return null;
    return whereWords(hit.number_role, hit.group_label, hit.group_label && hit.group_primary);
  };
  // THE SAVED NUMBER BEHIND A ROW — also when the row was fixed by hand before it went in (its AI read is kept as raw_read)
  const savedFor = (val) => (partNumbers || []).find((n) => sameVal(n.value, val))
    || (partNumbers || []).find((n) => n.raw_read && sameVal(n.raw_read, val) && !!numbers?.return_id && n.ai_research_id === numbers.return_id)
    || null;
  const placementOf = (val) => placedAs[val] || savedPlacement(val) || null;
  const savedNumberId = (val) => (savedFor(val) || {}).id || null;
  // ONE OF EACH, NEVER TWO SILENTLY (his instruction, 2026-10-17): *"If I try to add a second, stop
  // me with a plain notice: 'The assembly already has OEM # 123456 — replace it, or keep it?'"*
  const askOrDo = (existing, next, what, owner, run) => {
    if (existing && !sameVal(existing, next)) {
      setReplaceAsk({ existing, next, what, owner, run });
      return;
    }
    run();
  };
  const isSettled = (val) => !!placementOf(val) || skippedNums.has(val);
  const markPlaced = (val, where, how = null) => {
    setPlacedAs((s2) => ({ ...s2, [val]: where }));
    if (how) setPlacedHow((s2) => ({ ...s2, [val]: how }));
  };

  // ======================================================================= UNDO ON EVERY CLICK
  // His instruction, 2026-10-17: *"ASK AI FIRST: undo on every click."* Nothing he does in here is
  // one-way. A number saved on the part is deleted (audited, same as the Numbers editor); a value
  // put in the form goes back to exactly what the field held before; a search word is removed.
  // TAKE ONE PLACEMENT BACK OUT — shared by Undo and by the nudge's Switch. True when it is out.
  const takeOut = async (val) => {
    const how = placedHow[val];
    const saved = savedFor(val);
    // ONLY WHAT THIS READ PUT THERE — this visit's click, or a number saved off this same read (it carries the
    // read's id), so Undo still works after a reload. A number that came from anywhere else is not touched.
    const fromThisRead = !!saved && !!numbers?.return_id && saved.ai_research_id === numbers.return_id;
    try {
      if (how?.kind === "field" && onUndoSuggestion) {
        onUndoSuggestion(how.field);
      } else if (saved && ((how?.kind === "number" && addedToList.has(val)) || fromThisRead)) {
        const nid = saved.id;
        await api.delete(`/parts/${partId}/numbers/${nid}`);
        onNumberAdded?.();
      } else if (saved) {
        toast.error("That number was already on the part before this read — take it off in the Numbers list.");
        return false;
      } else if (sameVal(oemNumber, val) || sameVal(tagSerial, val)) {
        toast.error("It is already in the form as the part's own number — change it there.");
        return false;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't take it back out.");
      return false;
    }
    setPlacedAs((s2) => { const n = { ...s2 }; delete n[val]; return n; });
    setPlacedHow((s2) => { const n = { ...s2 }; delete n[val]; return n; });
    setInsertedNums((s2) => { const n = new Set(s2); ["oem_number", "tag_serial"].forEach((f) => n.delete(`${f}:${val}`)); return n; });
    setAddedToList((s2) => { const n = new Set(s2); n.delete(val); return n; });
    setReopened((s2) => { const n = new Set(s2); n.delete(val); return n; });
    // a kept answer goes with the placement it was about — placed again, the nudge may speak again
    if ((layout.kept_mine || []).some((k) => sameVal(k, val))) {
      const kept = layout.kept_mine.filter((k) => !sameVal(k, val));
      setLayout((l) => ({ ...l, kept_mine: kept }));
      saveLayout({ kept });
    }
    return true;
  };
  const undoPlacement = async (val, label) => {
    setUndoBusy(val);
    const ok = await takeOut(val);
    if (ok) {
      scanAccept(`${val} TAKEN BACK OUT`, { say: "undone" });
      toast.success(`Taken back out — ${label || val} is not on the part.`);
    }
    setUndoBusy(null);
    return ok;
  };

  // A SUGGESTION PUT IN THE FORM GOES BACK OUT THE SAME WAY — the field returns to exactly what it
  // held before, and the amber "AI, unverified" stamp goes with it.
  const undoField = (field, label) => {
    if (onUndoSuggestion?.(field) === false || !onUndoSuggestion) { toast.error("Couldn't take it back out."); return; }
    setApplied((s2) => { const n = new Set(s2); n.delete(field); return n; });
    toast.success(`${label} taken back out — the field is as it was.`);
  };

  const undoKeyword = (val, key) => {
    onRemoveKeyword?.(val);
    setAddedKw((s2) => { const n = new Set(s2); n.delete(key || val); return n; });
    toast.success(`Search word "${val}" taken back out.`);
  };
  // The part number / serial a component tag already carries, so a second one asks first.
  const groupSaved = (gi, g, role) => {
    const gid = groupKey(gi);
    const name = groupName(g, gi);
    const hit = (partNumbers || []).find((n) => (n.group_id === gid || (n.group_label && n.group_label === name))
      && (role === "primary" ? n.group_primary : n.number_role === "serial"));
    if (hit) return hit.value;
    // this session: a value placed as this group's part number / serial
    const want = role === "primary" ? whereWords("service_part", name, true) : whereWords("serial", name, false);
    const found = Object.entries(placedAs).find(([, where]) => where === want);
    return found ? found[0] : null;
  };

  // "Add a #": push the read number into the part's findability Numbers list.
  // THREE THINGS FIXED HERE, 2026-10-08 (behaviour only — this screen's layout is untouched):
  //  1. the owner was sent as `number_type`, a field the API does not have, so EVERY AI-added
  //     number landed as owner "unknown" — we never recorded that it was a component maker's;
  //  2. the confidence and the unsure characters were thrown away (production: ocr_confidence was
  //     empty on all 3,334 parts), so no clean-up list could ever be worked from the parts screen;
  //  3. nothing said WHO read it, so an AI read could be promoted to the part's identity. It now
  //     says read_by: "ai" — unless the human corrected the characters first, in which case the
  //     value is theirs and the AI's original read is kept as raw_read.
  const addToNumbers = async (i, value, n, edited, opts = {}) => {
    const v = (value || "").trim();
    if (!v) return;
    setAddingNum((s) => ({ ...s, [i]: true }));
    try {
      await api.post(`/parts/${partId}/numbers`, {
        value: v,
        owner: opts.owner || "unknown",
        source: "from_tag",
        read_by: edited ? "person" : "ai",
        // WHICH TAG IT CAME OFF, and WHAT IT IS (his decisions 1(b) and 3(b), 2026-10-13). A number
        // carrying a group belongs to a bolt-on COMPONENT and can never become the assembly's own
        // part number by itself; one number per group is that component's part number.
        number_role: opts.role || "service_part",
        group_id: opts.group_id || null,
        group_label: opts.group_label || null,
        group_photo_id: opts.group_photo_id || null,
        group_primary: opts.primary ? true : null,
        photo_file_id: opts.photo_file_id || n?.photo_id || opts.group_photo_id || null,
        ocr_confidence: n?.confidence || null,
        raw_read: n?.raw_read || n?.value || null,
        unsure_chars: n?.ambiguous_chars || [],
        // A poor read keeps its red stamp on the part — unless the person retyped the
        // characters, in which case the value is theirs and the warning is spent.
        needs_retake: edited ? false : !!n?.needs_retake,
        retake_reason: edited ? null : (n?.retake_reason || null),
        ai_research_id: numbers?.return_id || null,
      });
      setAddedToList((s) => new Set(s).add(v));
      markPlaced(v, whereWords(opts.role || "service_part", opts.group_label, opts.primary), { kind: "number" });
      logDecision(numbers?.return_id, "Added to Numbers list", v);
      toast.success(`Added ${v} to the part's Numbers list`);
      onNumberAdded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add the number.");
    }
    setAddingNum((s) => ({ ...s, [i]: false }));
  };

  // ============================================== THE AI SAID SOMETHING ELSE — SWITCH, OR KEEP MINE
  // What the person made of the number, in the same classes the AI uses: the part's own fields, else the role it
  // was saved with. Nothing saved = nothing to disagree with.
  const personClassOf = (val) => {
    if (oemNumber && sameVal(oemNumber, val)) return "part";
    if (tagSerial && sameVal(tagSerial, val)) return "serial";
    const hit = savedFor(val);
    // a number the SYSTEM moved (a replaced serial kept as "printed on it") is nobody's answer — no nudge
    if (!hit || hit.role_set_by === "system") return null;
    return classOfRole(hit.number_role || "service_part");
  };
  // SWITCH = his placement comes out exactly as Undo takes it out, and the number goes in as the AI said — through
  // the same buttons, so one part number and one serial still ask "replace it, or keep it?" first.
  const switchToAi = (val, n, i, group, aiClass, edited) => {
    const g = group?.g || null;
    const inComponent = !!g && !isAssembly(g);
    const m = metaFor(n.value) || {};
    const opts = {
      ...(inComponent ? { group_id: groupKey(group.gi), group_label: groupName(g, group.gi), group_photo_id: g.photo_id || null } : {}),
      photo_file_id: m.photo_id || n.photo_id || g?.photo_id || null,
    };
    const owner = inComponent ? groupName(g, group.gi) : "The assembly";
    let existing = null; let what = "part number"; let place;
    if (aiClass === "part") {
      existing = inComponent ? groupSaved(group.gi, g, "primary") : oemNumber;
      place = inComponent ? () => addToNumbers(i, val, n, edited, { ...opts, role: "service_part", primary: true })
        : () => insertNumber("oem_number", val, "OEM #", n);
    } else if (aiClass === "serial") {
      what = "serial number";
      existing = inComponent ? groupSaved(group.gi, g, "serial") : tagSerial;
      place = inComponent ? () => addToNumbers(i, val, n, edited, { ...opts, role: "serial" })
        : () => insertNumber("tag_serial", val, "Serial / tag #", n);
    } else {
      place = () => addToNumbers(i, val, n, edited, { ...opts, role: aiClass });
    }
    const run = async () => {
      setNudgeBusy(val);
      if (await takeOut(val)) {
        await place();
        logDecision(numbers?.return_id, `Switched to the AI's answer (${classWords(aiClass)})`, val);
      }
      setNudgeBusy(null);
    };
    askOrDo(existing && !sameVal(existing, val) ? existing : null, val, what, owner, run);
  };
  const keepMine = (val, aiClass) => {
    const next = [...(layout.kept_mine || []).filter((k) => !sameVal(k, val)), val];
    setLayout((l) => ({ ...l, kept_mine: next }));
    saveLayout({ kept: next });
    api.post(`/parts/${partId}/ai-research/decision`, {
      conclusion: "rejected", suggestion_ref: `ai_class:${val}`, entered_value: val, return_id: numbers?.return_id || null,
      note: `Kept my answer — the AI thought it was a ${classWords(aiClass)}`,
    }).catch(() => {});
    toast.success(`Kept your answer for ${val}`, { action: { label: "Undo", onClick: () => {
      const back = next.filter((k) => !sameVal(k, val));
      setLayout((l) => ({ ...l, kept_mine: back }));
      saveLayout({ kept: back });
    } } });
  };

  // inline zoom/pan/rotate handlers (mirror the Identify PhotoWorkspace)
  const stepPhoto = (dir) => { setActive((i) => (i + dir + list.length) % list.length); photoG.reset(); };

  const addKw = (value, key, scope = null) => {
    if (!onAddKeyword) return;
    onAddKeyword(value, scope);
    setAddedKw((s) => new Set(s).add(key || value));
    toast.success(`Added keyword “${value}”`);
  };

  // EVERY NUMBER SHOWS THE PHOTO IT WAS READ FROM, AND THE PHOTO OPENS (his instruction,
  // 2026-10-14). The read used to leave the picture behind: a number on screen with no way back to
  // the shot it came off, so checking a doubtful read meant hunting the strip by eye.
  const showPhoto = (fileId) => {
    const idx = list.findIndex((p) => p.file_id === fileId);
    if (idx < 0) { toast.error("That photo is not on this part any more."); return; }
    setActive(idx);
    document.querySelector("[data-testid='ai-photo-main']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ======================================================================= TAG GROUPING
  // HIS IDEA, and the thing he cares about most: *"an assembly carries bolt-on parts, each with
  // its own tag covered in numbers"* — one Bosch tag reads `4534 001 996 · 14-20739-001 · 007473 ·
  // 220517 · 185 bar`, five numbers off ONE tag, each a different kind of thing. Grouped under the
  // tag they came from, he can see at a glance which number belongs to what.
  //
  // THREE RULES THIS LAYOUT KEEPS:
  //   1. ONE TAG LOOKS EXACTLY AS IT DOES TODAY. The headers only appear when the AI actually
  //      found more than one tag, so the single-tag part his crew handles all day is unchanged.
  //   2. THE SAME ROWS AND THE SAME BUTTONS. The rows below are the same component, byte for byte
  //      — Use as OEM # / Use as Serial # / Add a # / Use as Keyword, every one of them, on every
  //      row. Nothing is added unless somebody clicks.
  //   3. THE GROUPING IS THE RELIABLE HALF; THE NAMES AND LABELS ARE GUESSES AND LOOK LIKE IT.
  //      The group is anchored to the PHOTO the tag was read in — stable and showable — and the
  //      AI's words are a caption, not the thing the data hangs on. A number in the wrong group is
  //      a one-tap Move, not a wrong part number.
  const allNums = numbers?.numbers || [];
  // THE PERSON'S OWN GROUPS. The AI misses a tag, or puts two tags' numbers together: ONE CLICK
  // makes a new group and the numbers get moved into it. No typing — the name is "Tag 4" unless
  // somebody wants to change it (his instruction, 2026-10-13).
  const aiGroups = (numbers?.tag_groups || []).filter((g) => (g.numbers || []).length);
  const tagGroups = [...aiGroups, ...extraGroups].map((g, gi) => (
    assemblyFlips.has(gi) ? { ...g, belongs_to: g.belongs_to === "assembly" ? "component" : "assembly" } : g
  ));
  const isAssembly = (g) => g?.belongs_to === "assembly";
  const metaFor = (value) => {
    for (const g of tagGroups) {
      const hit = (g.numbers || []).find((x) => String(x.value).toUpperCase() === String(value).toUpperCase());
      if (hit) return hit;
    }
    return null;
  };
  const groupedRows = () => {
    const idxOf = new Map(allNums.map((n, i) => [String(n.value).toUpperCase(), i]));
    const claimed = new Set();
    const blocks = tagGroups.map((g, gi) => {
      const rows = (g.numbers || [])
        .map((x) => idxOf.get(String(x.value).toUpperCase()))
        .filter((i) => i !== undefined)
        .filter((i) => (groupMoves[allNums[i].value] ?? gi) === gi);
      return { g, gi, rows };
    });
    // a number the person moved INTO this group
    Object.entries(groupMoves).forEach(([val, gi]) => {
      const i = idxOf.get(String(val).toUpperCase());
      const b = blocks[gi];
      if (i !== undefined && b && !b.rows.includes(i)) b.rows.push(i);
    });
    blocks.forEach((b) => b.rows.forEach((i) => claimed.add(i)));
    const loose = allNums.map((_, i) => i).filter((i) => !claimed.has(i));
    return { blocks, loose };
  };

  // ONE CLICK, NO TYPING: a new group appears named "Tag 4" and the numbers are moved into it.
  const saveLayout = (over = {}) => {
    if (!numbers?.return_id) return;
    api.put(`/parts/${partId}/ai-research/${numbers.return_id}/layout`, {
      moves: over.moves ?? groupMoves,
      extra_groups: (over.extra ?? extraGroups).map((g) => ({ belongs_to: g.belongs_to || "component" })),
      flips: Array.from(over.flips ?? assemblyFlips),
      kept_mine: over.kept ?? layout.kept_mine ?? [],
    }).then(({ data }) => { setLayout((l) => ({ ...l, names: data.names || l.names, kept_mine: data.kept_mine || [] })); loadTagNames(); })
      .catch(() => toast.error("Couldn't save how the tags are laid out — it may not be there after a reload."));
  };
  const addGroup = () => {
    const next = [...extraGroups, { name: "", belongs_to: "component", numbers: [], made_by_person: true }];
    setExtraGroups(next);
    saveLayout({ extra: next });
    toast.success("New tag group added — move the numbers that belong to it");
  };
  const moveTo = (value, gi) => {
    const next = { ...groupMoves, [value]: gi };
    setGroupMoves(next);
    saveLayout({ moves: next });
  };
  const flipAssembly = (gi) => {
    const next = new Set(assemblyFlips);
    if (next.has(gi)) next.delete(gi); else next.add(gi);
    setAssemblyFlips(next);
    saveLayout({ flips: next });
  };
  // The group a saved number is stamped with. Anchored to THIS read so two reads of the same
  // assembly never collide, and the label is the caption a person sees on the part.
  const groupKey = (gi) => `${numbers?.return_id || "read"}-g${gi}`;
  // "3 OF 5 PLACED", and clearly FINISHED when every number in the group is placed or passed over.
  const progressOf = (rows) => {
    const vals = rows.map((i) => numVal(i, allNums[i].value));
    const placed = vals.filter((v) => !!placementOf(v)).length;
    const skipped = vals.filter((v) => !placementOf(v) && skippedNums.has(v)).length;
    return { placed, skipped, total: vals.length, finished: vals.length > 0 && placed + skipped === vals.length };
  };
  // A HEADING A PERSON RENAMED WINS; otherwise the AI's words, otherwise "Tag 3"
  const renamedTo = (gi) => (layout.names || {})[String(gi)] || null;
  const groupName = (g, gi) => renamedTo(gi)?.name || (g?.name || "").trim() || `Tag ${gi + 1}`;
  const renameTo = async (gi, body) => {
    setNameBusy(gi);
    try {
      const { data } = await api.post(`/parts/${partId}/ai-research/${numbers.return_id}/heading`, { group_index: gi, ...body });
      onRenamed(gi, data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't name the tag."); }
    setNameBusy(null);
  };
  const tagInfo = (gi) => (tagNames?.groups || []).find((x) => x.group_index === gi) || null;
  // ONE TAP ACCEPTS THE SUGGESTION — a name off our list and a brand off ours; the assembly's own tag is named by the server
  const acceptName = (gi) => {
    const t = tagInfo(gi);
    if (!t?.suggested) return;
    renameTo(gi, t.is_assembly ? { assembly_tag: true } : { name: t.suggested.name, brand: t.suggested.brand || "" });
  };
  const onRenamed = (gi, data) => {
    setLayout((l) => ({ ...l, names: data.layout?.names || l.names }));
    loadTagNames();
    if (data.numbers_updated) onNumberAdded?.();
    if (data.name === data.was && !data.named) return;
    const aiWords = (tagGroups[gi]?.name || "").trim() || `Tag ${gi + 1}`;
    toast.success(`${data.named ? "Named" : "Put back to"} “${data.name}”${data.pending ? " (waiting for approval)" : ""}${data.brand ? ` · brand ${data.brand}` : ""}${data.numbers_updated ? ` — ${data.numbers_updated} saved number${data.numbers_updated === 1 ? "" : "s"} follow it` : ""}`,
      { action: { label: "Undo", onClick: () => renameTo(gi, { name: data.was, put_back: data.was === aiWords, suggest: true }) } });
  };
  // What the AI thinks this value is, turned into the role it would be SAVED as. A low-confidence
  // label is not used — it lands as "other", findable as text, never as a part number.
  const roleFor = (m) => (m?.label && m.label_confidence !== "low" ? m.label : "other");

  // "ADD THIS WHOLE TAG" — his one-click assisted selection. The AI's pick becomes that component's
  // part number, a second maker's number rides along as an interchange, and every other value lands
  // as the labelled number it is. NOTHING IS VERIFIED BY THIS: each one still needs its own ✓verify
  // on the part (his rule, unchanged — there is no "verify all" anywhere).
  // A NUMBER ALREADY DONE OR PASSED OVER IS LEFT ALONE (after a reload it used to be added a second time), and a
  // tag that would end up with two part numbers or two serials is not added in one go — those are picked one by one.
  const wholeTagTodo = (rows) => rows.filter((i) => {
    const v = numVal(i, allNums[i].value);
    return !isSettled(v) && !addedToList.has(v);
  });
  const addWholeTag = (g, gi, rows) => {
    const assembly = isAssembly(g);
    const plan = wholeTagTodo(rows).map((i) => {
      const n = allNums[i];
      const m = metaFor(n.value) || {};
      return { i, n, m, v: numVal(i, n.value), role: (m.suggested_primary || m.suggested_aka) ? "service_part" : roleFor(m),
        primary: !assembly && !!m.suggested_primary };
    });
    if (!plan.length) { toast("Every number on this tag is already done."); return; }
    if (!assembly) {
      const prim = plan.filter((x) => x.primary);
      const ser = plan.filter((x) => x.role === "serial");
      const hasPrim = groupSaved(gi, g, "primary");
      const hasSer = groupSaved(gi, g, "serial");
      if (prim.length > 1 || ser.length > 1 || (prim.length && hasPrim) || (ser.length && hasSer)) {
        toast.error(`That would give ${groupName(g, gi)} a second part number or serial number. Add those one at a time so you can choose.`);
        return;
      }
    }
    plan.forEach(({ i, n, m, v, role, primary }) => {
      addToNumbers(i, v, n, numEdits[i] !== undefined && numEdits[i] !== n.value, {
        role,
        owner: m.suggested_aka ? "oem" : "unknown",
        primary,
        group_id: assembly ? null : groupKey(gi),
        group_label: assembly ? null : groupName(g, gi),
        group_photo_id: g.photo_id || null,
        photo_file_id: m.photo_id || n.photo_id || g.photo_id || null,
      });
    });
  };

  const renderNumbers = () => {
    // ONE TAG LOOKS EXACTLY AS IT DOES TODAY — same rows, same buttons, no headers. The only
    // addition is the quiet way out when the AI has run two tags together.
    if (tagGroups.length < 2) {
      return (
        <>
          {allNums.map((n, i) => numberRow(n, i))}
          {addNumberPanel(-1, null)}
          {allNums.length >= 2 && (
            <Button data-testid="ai-tag-group-new-flat" type="button" size="sm" variant="ghost" onClick={addGroup}
              className="h-8 text-xs rounded-lg text-slate-500">
              <ListPlus className="w-3.5 h-3.5 mr-1" />These are two different tags?
            </Button>
          )}
        </>
      );
    }
    const { blocks, loose } = groupedRows();
    // EVERY GROUP STARTS CLOSED (his call, 23 Oct — it replaces "all open", 13 Oct): everything open at once was
    // confusing. A closed tag is a summary — photo, name, progress, the red stamp, its numbers in one line — and
    // step 1 (naming) is done right on the closed header. Tap a tag to open it; "Open all tags" is still there.
    const allGi = [...blocks.map((b) => b.gi), -1];
    const isOpen = (gi) => (closedGroups ? !closedGroups.has(gi) : false);
    const anyOpen = blocks.some((b) => isOpen(b.gi)) || isOpen(-1);
    const toggle = (gi) => setClosedGroups((prev) => {
      const base = new Set(prev || allGi);
      if (base.has(gi)) base.delete(gi); else base.add(gi);
      return base;
    });
    const dropOn = (gi) => ({
      onDragOver: (e) => { if (dragVal) e.preventDefault(); },
      onDrop: (e) => { e.preventDefault(); if (dragVal) { moveTo(dragVal, gi); toast.success(`Moved ${dragVal}`); } setDragVal(null); },
    });
    const assemblyBlock = (
      // THE ASSEMBLY ITSELF, AT THE TOP — the part he names and sells. Numbers stamped into its
      // casting or body live here, and this is the ONLY place "Use as OEM #" and "Use as Serial #"
      // are offered, so a bolt-on component's number can never quietly become the assembly's.
      <div data-testid="ai-tag-group-loose" {...dropOn(-1)}
        className={`rounded-xl border-2 bg-white overflow-hidden ${dragVal ? "border-dashed border-sky-300" : "border-slate-300"}`}>
        <button type="button" data-testid="ai-tag-group-toggle-loose" onClick={() => toggle(-1)}
          className={`w-full text-left bg-amber-50/60 px-3 py-2.5 ${isOpen(-1) ? "border-b border-slate-200" : ""}`}>
          <div className="flex items-baseline gap-2 flex-wrap">
            {isOpen(-1)
              ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-800">The assembly itself</span>
            <span className="font-mono text-[10px] text-slate-500">{loose.length}</span>
            {(() => {
              const pr = progressOf(loose);
              if (!loose.length) return null;
              return pr.finished ? (
                <span data-testid="ai-tag-group-finished-loose" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5">
                  <CheckCircle2 className="w-3 h-3" />finished{pr.skipped ? ` · ${pr.skipped} not needed` : ""}
                </span>
              ) : (
                <span data-testid="ai-tag-group-progress-loose" className="text-[10px] font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
                  {pr.placed} of {pr.total} placed{pr.skipped ? ` · ${pr.skipped} not needed` : ""}
                </span>
              );
            })()}
            <span className="text-[10px] text-amber-800">the part you're naming and selling</span>
            {!isOpen(-1) && loose.some((i) => allNums[i].needs_retake) && (
              <span data-testid="ai-tag-group-red-loose" className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                <AlertOctagon className="w-3 h-3" />check the photo
              </span>
            )}
          </div>
          {isOpen(-1)
            ? <div className="text-[11px] text-slate-500 mt-0.5">Read off the casting or the body of the part, not a plate. Only these can become the assembly's OEM or Serial #.</div>
            : (
              <div className="mt-0.5">
                <div data-testid="ai-tag-group-preview-loose" className="font-mono text-[11px] text-slate-600 truncate">
                  {loose.map((i) => allNums[i].value).join("  ·  ")}
                </div>
              </div>
            )}
        </button>
        {isOpen(-1) && (
          <div className="p-2.5 space-y-2 bg-slate-50/70">
            {loose.length === 0
              ? <div data-testid="ai-tag-group-loose-empty" className="text-[11px] text-slate-500 px-1 py-2">
                  Nothing was read off the assembly itself — every number is on a bolt-on's tag. The assembly keeps
                  no part number of its own unless you deliberately give it one with <b>“make this the assembly's number”</b>.
                </div>
              : loose.map((i) => numberRow(allNums[i], i, { gi: null, g: null, groups: blocks.map((b) => b.g) }))}
            {addNumberPanel(-1, null)}
          </div>
        )}
      </div>
    );
    return (
      <div className="space-y-3" data-testid="ai-tag-groups">
        <TagStepBar named={tagNames?.named || 0} total={blocks.length} waiting={tagNames?.waiting_for_approval || 0}
          placed={blocks.filter((b) => progressOf(b.rows).finished).length} />
        <div className="flex items-start justify-between gap-2">
          <div className="text-[11px] text-slate-500">
            The AI found <b>{blocks.length} tags</b> in these photos and kept each tag's numbers together.
            The grouping is its best read of what sits where — drag a number to the right tag, or use <b>Move to…</b>.
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button data-testid="ai-tag-group-new" type="button" size="sm" variant="outline" onClick={addGroup}
              className="h-8 text-xs rounded-lg border-sky-200 text-sky-700 hover:bg-sky-50">
              <ListPlus className="w-3.5 h-3.5 mr-1" />New tag group
            </Button>
            <ChoiceButton data-testid="ai-tag-groups-open-all"
              onClick={() => setClosedGroups(anyOpen ? new Set(allGi) : new Set())}>
              {anyOpen ? "Close all tags" : "Open all tags"}
            </ChoiceButton>
          </div>
        </div>
        {assemblyBlock}
        {blocks.map(({ g, gi, rows }) => (
          <div key={gi} data-testid={`ai-tag-group-${gi}`} {...dropOn(gi)}
            className={`rounded-xl border bg-white overflow-hidden ${dragVal ? "border-dashed border-sky-300" : isAssembly(g) ? "border-amber-200" : "border-slate-200"}`}>
            <div className={`flex items-start gap-3 flex-wrap bg-slate-50 px-3 py-2.5 ${isOpen(gi) ? "border-b border-slate-200" : ""}`}>
              {/* THE GROUP IS ANCHORED TO THE PHOTO THE TAG WAS READ IN — so the header can show
                  the actual tag, and the AI's name for it is only a caption. A photo is a fact; a
                  name is an opinion. */}
              {(() => {
                const ph = g.photo_id ? { file_id: g.photo_id } : list[g.photo_index ?? -1];
                return (
                  <div data-testid={`ai-tag-group-photo-${gi}`} className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-white grid place-items-center shrink-0">
                    {ph?.file_id
                      ? <AuthImg fileId={ph.file_id} thumb className="w-full h-full object-cover" alt={g.name || `tag ${gi + 1}`} />
                      : <ImageOff className="w-5 h-5 text-slate-300" />}
                  </div>
                );
              })()}
              {/* COLLAPSED IS A SUMMARY, NEVER A HIDING PLACE: a closed tag still shows its numbers
                  and still shows the red stamp and the count of unclear characters inside it. */}
              <button type="button" data-testid={`ai-tag-group-toggle-${gi}`} onClick={() => toggle(gi)}
                className="min-w-[170px] flex-1 text-left flex items-start gap-1.5 cursor-pointer">
                {isOpen(gi)
                  ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-1" />
                  : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-1" />}
                <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span data-testid={`ai-tag-group-name-${gi}`} className="text-[11px] font-bold uppercase tracking-widest text-slate-700">{groupName(g, gi)}</span>
                  <span className="font-mono text-[10px] text-slate-500">{rows.length} number{rows.length === 1 ? "" : "s"}</span>
                  {(() => {
                    const pr = progressOf(rows);
                    if (!rows.length) return null;
                    return pr.finished ? (
                      <span data-testid={`ai-tag-group-finished-${gi}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5">
                        <CheckCircle2 className="w-3 h-3" />finished{pr.skipped ? ` · ${pr.skipped} not needed` : ""}
                      </span>
                    ) : (
                      <span data-testid={`ai-tag-group-progress-${gi}`} className="text-[10px] font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
                        {pr.placed} of {pr.total} placed{pr.skipped ? ` · ${pr.skipped} not needed` : ""}
                      </span>
                    );
                  })()}
                  {isAssembly(g) && <span data-testid={`ai-tag-group-assembly-${gi}`} className="text-[10px] font-bold uppercase tracking-widest text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">the assembly's own tag</span>}
                  {!tagInfo(gi)?.named && g.name && !g.made_by_person && <span className="text-[10px] text-slate-400">the AI's words: “{(g.name || "").trim()}”</span>}
                  {!tagInfo(gi)?.named && g.made_by_person && <span className="text-[10px] text-slate-400">your group</span>}
                  {!isOpen(gi) && rows.some((i) => allNums[i].needs_retake) && (
                    <span data-testid={`ai-tag-group-red-${gi}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                      <AlertOctagon className="w-3 h-3" />check the photo
                    </span>
                  )}
                </div>
                {isOpen(gi)
                  ? (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {isAssembly(g)
                        ? "The assembly's own plate — these can become its OEM or Serial #."
                        : "A bolt-on with its own tag. One number here is this component's part number; the rest are labelled values that still find the assembly."}
                    </div>
                  )
                  : (
                    <div className="mt-0.5">
                      <div data-testid={`ai-tag-group-preview-${gi}`} className="font-mono text-[11px] text-slate-600 truncate">
                        {rows.map((i) => allNums[i].value).join("  ·  ")}
                      </div>
                      {rows.some((i) => (allNums[i].ambiguous_chars || []).length) && (
                        <div className="text-[10px] text-amber-700 mt-0.5">
                          {rows.filter((i) => (allNums[i].ambiguous_chars || []).length).length} with unclear characters — open to check them.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </button>
              <div className="flex flex-col items-end gap-1 shrink-0 ml-auto">
                {/* ADD THIS WHOLE TAG — the AI's pick becomes this component's part number and the
                    rest land labelled. It is SELECTION, never verification: every one still needs
                    its own ✓verify on the part. */}
                <Button data-testid={`ai-tag-group-add-all-${gi}`} type="button" size="sm" variant="outline" disabled={!wholeTagTodo(rows).length}
                  onClick={() => addWholeTag(g, gi, rows)}
                  className="rounded-lg h-9 text-xs border-sky-200 text-sky-700 hover:bg-sky-50">
                  <ListPlus className="w-3.5 h-3.5 mr-1" />Add this whole tag
                </Button>
                {/* THE DELIBERATE SWITCH (his decision 3(b)): if this plate IS the assembly's own,
                    somebody says so on purpose — the AI never promotes a component to the assembly. */}
                <ChoiceButton data-testid={`ai-tag-group-is-assembly-${gi}`} onClick={() => flipAssembly(gi)} className="h-8 min-h-8">
                  {isAssembly(g) ? "No, it's a bolt-on component" : "This tag is the assembly's own"}
                </ChoiceButton>
              </div>
              {/* STEP 1 RIGHT ON THE CLOSED HEADER — one tap accepts; pick another opens our names list */}
              <TagNameChoice gi={gi} info={tagInfo(gi)} busy={nameBusy === gi}
                onAccept={() => acceptName(gi)} onPick={() => setRenameFor(gi)} />
            </div>
            {isOpen(gi) && (
              <div className="p-2.5 space-y-2 bg-slate-50/70">
                {rows.length === 0
                  ? <div data-testid={`ai-tag-group-empty-${gi}`} className="text-[11px] text-slate-400 px-1 py-2">Drag a number here, or use <b>Move to…</b> on the row.</div>
                  : rows.map((i) => numberRow(allNums[i], i, { gi, g, groups: blocks.map((b) => b.g) }))}
                {addNumberPanel(gi, g)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };


  // ====================================================== A NUMBER THE AI NEVER SAW
  // His case, 2026-10-17: *"the ECM's OEM number was on a silver steel tag the AI didn't read. I
  // read it myself, but couldn't add it under the ECM."* Type it, scan it with the camera or the
  // gun, say what it is — and it is stamped as entered by a PERSON, not read by the AI.
  const addOwnNumber = async (value, gi, g) => {
    const v = (value || "").trim();
    if (!v) return;
    const inComponent = gi !== -1 && !!g && !isAssembly(g);
    const role = addNumRole;
    const label = inComponent ? groupName(g, gi) : "The assembly";
    const ownWhere = whereWords(role, inComponent ? groupName(g, gi) : null, inComponent && role === "service_part");
    const go = async () => {
      setAddNumBusy(true);
      try {
        if (!inComponent && role === "service_part") {
          insertNumber("oem_number", v, "OEM #", null);
        } else if (!inComponent && role === "serial") {
          insertNumber("tag_serial", v, "Serial / tag #", null);
        } else {
          await api.post(`/parts/${partId}/numbers`, {
            value: v, owner: "unknown", source: "from_tag", read_by: "person",
            number_role: role,
            group_id: inComponent ? groupKey(gi) : null,
            group_label: inComponent ? groupName(g, gi) : null,
            group_photo_id: inComponent ? (g.photo_id || null) : null,
            group_primary: inComponent && role === "service_part" ? true : null,
            ai_research_id: numbers?.return_id || null,
          });
          markPlaced(v, ownWhere, { kind: "number" });
          setAddedToList((s2) => new Set(s2).add(v));
          onNumberAdded?.();
          toast.success(`Added ${v} — entered by you, not read by the AI`);
        }
        const where = !inComponent && role === "service_part" ? "the assembly's part number"
          : !inComponent && role === "serial" ? "the assembly's serial number" : ownWhere;
        setOwnAdded((s2) => ({ ...s2, [gi]: [...(s2[gi] || []).filter((x) => !sameVal(x.value, v)), { value: v, where }] }));
        setAddNumOpen(null);
        scanAccept(`${v} ADDED`, { say: "added" });
      } catch (e) {
        scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add the number.");
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add the number.");
      }
      setAddNumBusy(false);
    };
    const existing = role === "service_part"
      ? (inComponent ? groupSaved(gi, g, "primary") : oemNumber)
      : role === "serial" ? (inComponent ? groupSaved(gi, g, "serial") : tagSerial) : null;
    askOrDo(existing, v, role === "serial" ? "serial number" : "part number", label, go);
  };

  // TYPED IN BY HAND STAYS LISTED AFTER A RELOAD — rebuilt from what this read saved with no AI read behind it
  const ownAddedFor = (gi, g) => {
    const session = ownAdded[gi] || [];
    const inComponent = gi !== -1 && !!g && !isAssembly(g);
    const rid = numbers?.return_id;
    if (!rid || (gi !== -1 && !inComponent)) return session;
    const saved = (partNumbers || []).filter((n) => n.read_by === "person" && n.ai_research_id === rid && !n.raw_read
      && (inComponent ? n.group_id === groupKey(gi) : !n.group_id)
      && !allNums.some((a) => sameVal(a.value, n.value)) && !session.some((x) => sameVal(x.value, n.value)));
    return [...session, ...saved.map((n) => ({ value: n.value, where: placementOf(n.value) }))];
  };

  const addNumberPanel = (gi, g) => (
    addNumOpen === gi ? (
      <div data-testid={`ai-add-own-panel-${gi}`} className="rounded-xl border-2 border-sky-200 bg-sky-50/60 p-2.5 space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-widest text-sky-900">
          Add a number {gi === -1 || isAssembly(g) ? "to the assembly" : `to ${groupName(g, gi)}`}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {[["service_part", "Part #"], ["serial", "Serial #"], ["other", "Other"]].map(([r, lbl]) => (
            <Button key={r} data-testid={`ai-add-own-role-${gi}-${r}`} type="button" size="sm"
              variant={addNumRole === r ? "default" : "outline"} onClick={() => setAddNumRole(r)}
              className={`rounded-lg h-8 text-xs ${addNumRole === r ? "bg-sky-700 hover:bg-sky-800 text-white" : ""}`}>
              {lbl}
            </Button>
          ))}
        </div>
        <ScanField testid={`ai-add-own-${gi}`} label="" submitLabel={addNumBusy ? "Adding…" : "Add it"}
          placeholder="Type it, scan it with the camera, or use the gun…" busy={addNumBusy} uppercase
          onScan={(v) => addOwnNumber(v, gi, g)} />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-sky-900">It is marked <b>entered by you</b> — the AI never saw it. It still needs its own ✓verify.</span>
          <Button data-testid={`ai-add-own-cancel-${gi}`} type="button" size="sm" variant="ghost"
            onClick={() => setAddNumOpen(null)} className="h-7 px-2 text-[11px] text-slate-500">Cancel</Button>
        </div>
      </div>
    ) : (
      <div className="space-y-1.5">
        {/* WHAT YOU TYPED IN STAYS IN SIGHT, WITH ITS UNDO — it has no read row of its own */}
        {ownAddedFor(gi, g).filter((x) => placementOf(x.value)).map((x) => (
          <div key={x.value} data-testid={`ai-own-added-${gi}-${x.value}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-100/80 px-2.5 py-1.5 text-[11px] text-slate-600">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span className="font-mono font-bold text-slate-700">{x.value}</span>
            <span>entered by you — went in as {placementOf(x.value)}</span>
            <ChoiceButton tone="danger" data-testid={`ai-own-added-undo-${gi}-${x.value}`} disabled={undoBusy === x.value}
              onClick={async () => { if (await undoPlacement(x.value, x.where)) setOwnAdded((s2) => ({ ...s2, [gi]: (s2[gi] || []).filter((y) => y.value !== x.value) })); }}>
              {undoBusy === x.value ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}Take it back out
            </ChoiceButton>
          </div>
        ))}
        <Button data-testid={`ai-add-own-open-${gi}`} type="button" size="sm" variant="outline"
          onClick={() => { setAddNumOpen(gi); setAddNumRole("service_part"); }}
          className="rounded-lg h-9 text-xs border-sky-200 text-sky-700 hover:bg-sky-50">
          <PlusCircle className="w-3.5 h-3.5 mr-1" />
          Add a number {gi === -1 || isAssembly(g) ? "the AI missed" : `to ${groupName(g, gi)}`}
        </Button>
      </div>
    )
  );

  // ONE ROW, used flat (one tag) and inside a group (several tags). WHICH BUTTONS IT SHOWS IS THE
  // WHOLE SAFETY RULE: the assembly's own values offer "Use as OEM #" and "Use as Serial #"; a
  // bolt-on component's values never do, so a component number cannot silently become the
  // assembly's part number (his decision 3(b), 2026-10-13).
  const numberRow = (n, i, group) => {
    const low = n.confidence === "low" || n.needs_retake;
    const val = numVal(i, n.value);
    const editing = editNum.has(i);
    const edited = numEdits[i] !== undefined && numEdits[i] !== n.value;
    const meta = group ? metaFor(n.value) : null;
    const g = group?.g || null;
    const inComponent = !!g && !isAssembly(g);
    const photoId = meta?.photo_id || n.photo_id || g?.photo_id || null;
    // THE PHOTO A NUMBER WAS READ FROM IS SAVED WITH IT, whichever button puts it on the part
    const groupOpts = inComponent
      ? { group_id: groupKey(group.gi), group_label: groupName(g, group.gi), group_photo_id: g.photo_id || null, photo_file_id: photoId }
      : { photo_file_id: photoId };
    // A REAL DATE OR A NUMBER WITH A REAL UNIT LEADS WITH "Date code on …" / "Rating on …" even when the AI didn't say
    // (the server saves it as one whichever button is pressed — 11/30/18 and 12 VOLT are never part numbers)
    const label = (meta?.label && meta.label_confidence !== "low" ? meta.label : null) || labelledShape(val) || null;
    const W = numberWords(inComponent);
    const others = (group?.groups || []).map((gg, gidx) => ({ gi: gidx, name: groupName(gg, gidx) })).filter((o) => o.gi !== group?.gi);
    if (group && group.gi !== null) others.push({ gi: -1, name: "The assembly itself" });
    // DONE LOOKS DONE. A placed number is greyed, ticked, and SAYS where it went; a number the
    // person has deliberately passed over is settled too, so a group can finish.
    const placedWhere = placementOf(val);
    const skipped = skippedNums.has(val);
    const settled = !!placedWhere || skipped;
    const showButtons = !settled || reopened.has(val);
    // THE GENTLE NUDGE: only where the AI clearly called it something else than where it went
    const aiClass = placedWhere ? aiClassOf(metaFor(n.value), inComponent) : null;
    const mine = aiClass ? personClassOf(val) : null;
    const nudge = !!aiClass && !!mine && aiClass !== mine && !(layout.kept_mine || []).some((k) => sameVal(k, val));
    return (
      <div key={i} data-testid={`ai-number-${i}`}
        draggable={!!group && !editing}
        onDragStart={() => setDragVal(n.value)} onDragEnd={() => setDragVal(null)}
        className={`rounded-xl border p-3 ${settled ? "bg-slate-100/80 border-slate-200" : "bg-white"} ${low && !settled ? "border-red-100" : ""} ${dragVal === n.value ? "opacity-50" : ""}`}>
        <div className={`flex items-center gap-2 flex-wrap ${low && !settled ? "opacity-80" : ""}`}>
          {photoId ? (
            <button type="button" data-testid={`ai-number-photo-${i}`} title={W.photoHint}
              onClick={() => showPhoto(photoId)}
              className="shrink-0 w-9 h-9 rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-[#E0781A] focus:outline-none focus:ring-2 focus:ring-[#E0781A]">
              <AuthImg fileId={photoId} alt="the photo this number was read from" className="w-full h-full object-cover" />
            </button>
          ) : (
            <Tag className="w-4 h-4 text-slate-400" />
          )}
          {editing ? (
            <Input data-testid={`ai-number-edit-${i}`} value={val} autoFocus
              onChange={(e) => setNumEdits((s) => ({ ...s, [i]: e.target.value }))}
              className="h-8 w-44 font-mono text-sm rounded-lg" />
          ) : (
            <span className={`font-mono font-bold ${settled ? "text-slate-400" : "text-slate-800"}`}>{val}</span>
          )}
          {placedWhere && (
            <Badge data-testid={`ai-number-done-${i}`}
              className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-bold">
              <CheckCircle2 className="w-3 h-3 mr-0.5" />Added as {placedWhere}
            </Badge>
          )}
          {skipped && !placedWhere && (
            <Badge data-testid={`ai-number-skipped-${i}`}
              className="rounded-full border border-slate-300 bg-slate-100 text-slate-500 text-[10px] font-bold">
              Not needed
            </Badge>
          )}
          {/* THE LABEL IS A SUGGESTION AND IS DRESSED AS ONE. `185 bar` is a rating because the
              unit is printed; `220517` being a date code is a GUESS off the shape of six digits.
              Never pre-selected, never applied, and absent entirely when the AI is unsure. */}
          {meta?.label && meta.label_confidence !== "low" && (
            <Badge data-testid={`ai-number-label-${i}`} title="The AI's guess at what this number is. Nothing is applied until you click." className="rounded-full border border-slate-200 bg-slate-50 text-slate-500 font-normal text-[10px]">
              likely {meta.label.replace("_", " ")}
            </Badge>
          )}
          {meta?.suggested_primary && inComponent && (
            <Badge data-testid={`ai-number-suggested-primary-${i}`} className="rounded-full border border-sky-200 bg-sky-50 text-sky-700 font-normal text-[10px]">
              likely this tag's part #
            </Badge>
          )}
          <Badge className={`rounded-full border text-[10px] ${CONF_META[n.confidence] || CONF_META.low}`}>{n.confidence} confidence</Badge>
          {n.needs_retake && <Badge className="rounded-full border border-red-200 bg-red-50 text-red-600 text-[10px]"><Camera className="w-3 h-3 mr-0.5" />check the photo</Badge>}
          {edited && <Badge data-testid={`ai-number-edited-${i}`} className="rounded-full border border-violet-200 bg-violet-50 text-violet-700 text-[10px]"><Pencil className="w-3 h-3 mr-0.5" />edited</Badge>}
          <ChoiceButton data-testid={`ai-number-edit-toggle-${i}`} className="ml-auto h-8 min-h-8"
            onClick={() => setEditNum((s) => { const nx = new Set(s); if (nx.has(i)) nx.delete(i); else nx.add(i); return nx; })}>
            <Pencil className="w-3.5 h-3.5 mr-1" />{editing ? "Done" : "Edit"}
          </ChoiceButton>
          {/* MOVING A NUMBER TO THE RIGHT TAG IS A ROW ACTION, so it sits with Edit rather than
              in the line of green buttons — those four are the ones that change the part. */}
          {others.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ChoiceButton data-testid={`ai-number-move-${i}`} className="h-8 min-h-8"><MoveRight className="w-3.5 h-3.5 mr-1" />Move to…</ChoiceButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="text-xs">
                {others.map((o) => (
                  <DropdownMenuItem key={o.gi} data-testid={`ai-number-move-${i}-to-${o.gi}`}
                    onClick={() => { moveTo(n.value, o.gi); toast.success(`Moved to ${o.name}`); }}>
                    Move to {o.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {editing && <div className="text-[11px] text-slate-400 mt-1">Fix a misread before you use it. Editing does NOT verify — you'll still confirm it against the tag.</div>}
        {(n.ambiguous_chars || []).length > 0 && <div className="text-[11px] text-amber-700 mt-1.5">Unclear chars: {n.ambiguous_chars.join(", ")} — verify against the physical stamp.</div>}
        {/* RED MEANS STOP — and it says what the read nearly matched, so the
            crew knows which number to compare against (his rule, 2026-10-13). */}
        {n.retake_reason && (
          <div data-testid={`ai-number-retake-reason-${i}`} className="mt-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-semibold text-[#B01E28] flex items-start gap-1.5">
            <AlertOctagon className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>
              {n.retake_reason}
              {/* ONE TAP SETTLES IT (his instruction, 2026-10-13): the look-alike and the part
                  holding it, side by side — no walk to the shelf to find out which it is. */}
              <ChoiceButton tone="danger" data-testid={`ai-number-see-twin-${i}`} className="ml-1.5 h-7 min-h-7"
                onClick={() => setTwinFor({ value: n.value, row: i })}>
                See them side by side
              </ChoiceButton>
            </span>
          </div>
        )}
        {n.raw_read && n.raw_read !== n.value && <div className="text-[11px] text-slate-400 mt-0.5 font-mono">raw: {n.raw_read}</div>}
        {!showButtons && (
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <span data-testid={`ai-number-settled-${i}`} className="text-[11px] text-slate-500">
              {placedWhere ? `Done — it went in as ${placedWhere}.` : "Passed over — nothing was saved."}
            </span>
            <ChoiceButton data-testid={`ai-number-reopen-${i}`} onClick={() => setReopened((s2) => new Set(s2).add(val))}>
              {placedWhere ? "Use it somewhere else too" : "Use it after all"}
            </ChoiceButton>
            {skipped && !placedWhere && (
              <ChoiceButton data-testid={`ai-number-unskip-${i}`}
                onClick={() => { setSkippedNums((s2) => { const nx = new Set(s2); nx.delete(val); return nx; }); logSkip(val, false); }}>
                <Undo2 className="w-3 h-3 mr-1" />Undo
              </ChoiceButton>
            )}
            {/* UNDO ON EVERY CLICK — nothing in here is one-way. */}
            {placedWhere && (
              <ChoiceButton tone="danger" data-testid={`ai-number-undo-${i}`}
                disabled={undoBusy === val} onClick={() => undoPlacement(val, placedWhere)}>
                {undoBusy === val ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}
                Take it back out
              </ChoiceButton>
            )}
            <ChoiceButton data-testid={`ai-copy-number-settled-${i}`} onClick={() => copyText(val)}>
              <Copy className="w-3 h-3 mr-1" />Copy
            </ChoiceButton>
          </div>
        )}
        {nudge && (
          <AiNudgeLine testid={`ai-nudge-${i}`} aiClass={aiClass} primary={inComponent && !!metaFor(n.value)?.suggested_primary}
            busy={nudgeBusy === val} onSwitch={() => switchToAi(val, n, i, group, aiClass, edited)} onKeep={() => keepMine(val, aiClass)} />
        )}
        <div className={`flex items-center gap-2 flex-wrap mt-2 ${showButtons ? "" : "hidden"}`}>
          {/* THE SAME FOUR CHOICES ON EVERY NUMBER, AND THE WORDS SAY WHERE IT GOES (his
              instruction, 2026-10-14). Part number · Serial number · Pick what this number is… ·
              Search word — identical on a component tag and on the assembly's own plate, with only
              the destination words changing. The two phrases nobody understood are gone.
              The one safety difference stays: a component's value writes to the component, and
              becoming the ASSEMBLY'S part number is a separate, deliberate, two-tap action. */}
          <HelpTip text={W.help.partNumber} testid={`ai-help-primary-${i}`}>
          <Button data-testid={inComponent ? `ai-add-group-primary-${i}` : `ai-insert-oem-${i}`}
            type="button" size="sm"
            disabled={inComponent ? (addedToList.has(val) || !!addingNum[i]) : insertedNums.has(`oem_number:${val}`)}
            onClick={() => (inComponent
              ? askOrDo(groupSaved(group.gi, g, "primary"), val, "part number", groupName(g, group.gi),
                () => addToNumbers(i, val, n, edited, { ...groupOpts, role: "service_part", primary: true }))
              : askOrDo(oemNumber, val, "part number", "The assembly",
                () => insertNumber("oem_number", val, "OEM #", n)))}
            className={`rounded-lg h-8 text-white text-xs ${inComponent ? "bg-sky-700 hover:bg-sky-800" : "bg-[#E0781A] hover:bg-[#C76714]"}`}>
            {addingNum[i] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : (inComponent ? addedToList.has(val) : insertedNums.has(`oem_number:${val}`))
                ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{W.partNumberDone}</>
                : <><PlusCircle className="w-3.5 h-3.5 mr-1" />{W.partNumber}</>}
          </Button>
          </HelpTip>
          <HelpTip text={W.help.serial} testid={`ai-help-serial-${i}`}>
          <Button data-testid={inComponent ? `ai-add-group-serial-${i}` : `ai-insert-serial-${i}`}
            type="button" size="sm" variant="outline"
            disabled={inComponent ? (addedToList.has(val) || !!addingNum[i]) : insertedNums.has(`tag_serial:${val}`)}
            onClick={() => (inComponent
              ? askOrDo(groupSaved(group.gi, g, "serial"), val, "serial number", groupName(g, group.gi),
                () => addToNumbers(i, val, n, edited, { ...groupOpts, role: "serial" }))
              : askOrDo(tagSerial, val, "serial number", "The assembly",
                () => insertNumber("tag_serial", val, "Serial / tag #", n)))}
            className="rounded-lg h-8 text-xs">
            {(inComponent ? false : insertedNums.has(`tag_serial:${val}`))
              ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{W.serialDone}</>
              : <><PlusCircle className="w-3.5 h-3.5 mr-1" />{W.serial}</>}
          </Button>
          </HelpTip>
          {/* ONE CLICK FOR THE AI'S OWN GUESS, dressed as a guess. `185 bar` is a rating because
              the unit is printed; six digits being a date code is a guess off the shape. */}
          <HelpTip text={meta?.suggested_aka ? W.help.otherMakers : W.help.another} testid={`ai-help-another-${i}`}>
          <Button data-testid={(meta?.suggested_aka || label) ? `ai-add-labelled-${i}` : `ai-add-number-${i}`}
            type="button" size="sm" variant="outline" disabled={addedToList.has(val) || !!addingNum[i]}
            onClick={() => addToNumbers(i, val, n, edited, meta?.suggested_aka
              ? { ...groupOpts, role: "service_part", owner: "oem" }
              : { ...groupOpts, role: label || "service_part" })}
            className="rounded-lg h-8 text-xs border-sky-200 text-sky-700 hover:bg-sky-50">
            <ListPlus className="w-3.5 h-3.5 mr-1" />
            {addedToList.has(val) ? "Added"
              : meta?.suggested_aka ? W.otherMakersPartNumber
                : label ? W.roleItem(label) : W.anotherPartNumber}
          </Button>
          </HelpTip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ChoiceButton data-testid={`ai-add-labelled-pick-${i}`} title={W.help.pick}
                disabled={addedToList.has(val)}>{W.pick} ▾</ChoiceButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="text-xs">
              {PICK_ROLES.map((r) => (
                <DropdownMenuItem key={r} data-testid={`ai-add-labelled-${i}-${r}`}
                  onClick={() => addToNumbers(i, val, n, edited, { ...groupOpts, role: r })}>
                  {W.roleItem(r)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* A SEARCH WORD BELONGS TO SOMETHING TOO (his instruction, 2026-10-14): the assembly's
              words stay on the assembly; a component's words travel with it when it is childed
              off. The button says which, so nobody has to guess. */}
          <HelpTip text={W.help.keyword} testid={`ai-help-keyword-${i}`}>
          <Button data-testid={`ai-number-keyword-${i}`} type="button" size="sm" variant="outline" disabled={addedKw.has(`num:${val}`)}
            onClick={() => addKw(val, `num:${val}`, inComponent ? { group_id: groupOpts.group_id, group_label: groupOpts.group_label } : null)}
            className="rounded-lg h-8 text-xs border-violet-200 text-violet-700 hover:bg-violet-50">
            {addedKw.has(`num:${val}`) ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{W.keywordDone}</> : <><Tag className="w-3.5 h-3.5 mr-1" />{W.keyword}</>}
          </Button>
          </HelpTip>
          {addedKw.has(`num:${val}`) && (
            <ChoiceButton tone="danger" data-testid={`ai-number-keyword-undo-${i}`} onClick={() => undoKeyword(val, `num:${val}`)}>
              <Undo2 className="w-3 h-3 mr-1" />Take the word back out
            </ChoiceButton>
          )}
          {/* THE DELIBERATE PATH, AND IT ASKS TWICE — two clicks, by design, so nobody makes a
              component's number the assembly's by brushing past a button. */}
          {inComponent && (
            <HelpTip text={W.help.promote} testid={`ai-help-promote-${i}`}>
            <ChoiceButton tone={promoting === val ? "amber" : "plain"} data-testid={`ai-promote-assembly-${i}`}
              onClick={() => {
                if (promoting !== val) { setPromoting(val); return; }
                setPromoting(null);
                askOrDo(oemNumber, val, "part number", "The assembly",
                  () => insertNumber("oem_number", val, "OEM #", n));
              }}>
              {promoting === val ? W.promoteConfirm : W.promote}
            </ChoiceButton>
            </HelpTip>
          )}
          <ChoiceButton data-testid={`ai-copy-number-${i}`} title={W.help.copy} onClick={() => copyText(val)}><Copy className="w-3.5 h-3.5 mr-1" />Copy</ChoiceButton>
          {/* DELIBERATELY PASSING ONE OVER SETTLES IT, so a group can read finished and he can move
              on with confidence. Nothing is saved; it can be undone. */}
          {!placedWhere && (
            <ChoiceButton data-testid={`ai-number-skip-${i}`}
              onClick={() => { setSkippedNums((s2) => new Set(s2).add(val)); setReopened((s2) => { const nx = new Set(s2); nx.delete(val); return nx; }); logSkip(val, true); }}>
              Not needed
            </ChoiceButton>
          )}
        </div>
      </div>
    );
  };

  const d = result?.describe || {};
  const conf = d.confidence || {};
  const abstained = !!result?.abstained;

  const exportFindings = () => {
    const c = context || {};
    const lines = ["AI FINDINGS"];
    const ctxBits = CTX_KEYS.filter((k) => c[k]).map((k) => `${k}: ${c[k]}`);
    if (tagSerial) ctxBits.push(`serial: ${tagSerial}`);
    if (ctxBits.length) lines.push("Context: " + ctxBits.join(" · "));
    lines.push("");
    if (d.suggested_name) lines.push(`Name: ${d.suggested_name}`);
    if (d.suggested_brand) lines.push(`Brand: ${d.suggested_brand}`);
    if (d.suggested_category) lines.push(`Category: ${d.suggested_category}`);
    if (d.suggested_part_type) lines.push(`Part type: ${d.suggested_part_type}`);
    if (d.description) lines.push(`Description: ${d.description}`);
    if ((d.suggested_keywords || []).length) lines.push(`Keywords: ${d.suggested_keywords.join(", ")}`);
    if ((d.observed_markings || []).length) lines.push(`Other markings: ${d.observed_markings.join(", ")}`);
    if ((numbers?.numbers || []).length) lines.push(`Numbers read: ${numbers.numbers.map((n) => `${n.value} (${n.confidence})`).join(", ")}`);
    lines.push("", "— AI Assist suggestions. Verify before use; confirm numbers against the tag.");
    copyText(lines.join("\n"), "AI findings copied — paste into your tool");
  };

  const confChip = (lvl) => lvl ? <Badge className={`rounded-full border text-[10px] ${CONF_META[lvl] || CONF_META.low}`}>{lvl}</Badge> : null;

  const FieldRow = ({ field, label, value, level }) => {
    if (!value) return null;
    const dim = abstained || level === "low";
    const showCreate = (field === "category" || field === "part_type" || field === "name" || field === "brand") && needsCreate[field] === value;
    return (
      <div className="space-y-1.5">
        <div data-testid={`ai-suggest-${field}`} className={`rounded-xl border bg-white p-3 flex items-start gap-2 ${dim ? "border-slate-200 opacity-70" : "border-slate-200"}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
              {confChip(level)}
            </div>
            <div className="text-sm font-semibold text-slate-800 break-words">{value}</div>
            {dim && <div className="text-[11px] text-amber-700 mt-0.5">Low confidence — check the photo before using.</div>}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Button data-testid={`ai-use-${field}`} type="button" size="sm" disabled={applied.has(field)}
              onClick={() => use(field, value, label)}
              className="rounded-lg h-8 bg-[#E0781A] hover:bg-[#C76714] text-white text-xs">
              {applied.has(field) ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Used</> : <><PlusCircle className="w-3.5 h-3.5 mr-1" />Use</>}
            </Button>
            {applied.has(field) && (
              <ChoiceButton tone="danger" data-testid={`ai-undo-${field}`} onClick={() => undoField(field, label)}>
                <Undo2 className="w-3 h-3 mr-1" />Take it back out
              </ChoiceButton>
            )}
          </div>
        </div>
        {showCreate && (
          <div data-testid={`ai-create-${field}`} className="rounded-lg border border-violet-200 bg-violet-50 p-2.5 flex items-center gap-2 flex-wrap">
            <span className="text-[12px] text-violet-800 flex-1">“{value}” isn't in the {label.toLowerCase()} list yet.</span>
            {/* THE ONE ACTION IN HERE WITH NO WAY BACK: a new value on a shared list everybody
                picks from. So it asks once, like promoting a number to the assembly does. */}
            <Button data-testid={`ai-create-btn-${field}`} type="button" size="sm"
              onClick={() => {
                if (confirmCreate !== `${field}:${value}`) { setConfirmCreate(`${field}:${value}`); return; }
                setConfirmCreate(null);
                createList(field, value, label);
              }}
              className={`rounded-lg h-8 text-xs shrink-0 ${confirmCreate === `${field}:${value}`
                ? "bg-amber-500 hover:bg-amber-600 text-white font-bold" : "bg-violet-600 hover:bg-violet-700 text-white"}`}>
              <PlusCircle className="w-3.5 h-3.5 mr-1" />
              {confirmCreate === `${field}:${value}`
                ? `Yes — add it to the ${label.toLowerCase()} list for everyone`
                : `Add “${value}” to the list`}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const GroupTitle = ({ children, action }) => (
    <div className="flex items-center justify-between mt-1">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{children}</span>
      {action}
    </div>
  );

  const hasIdentity = d.suggested_name || d.suggested_brand || d.suggested_category || d.suggested_part_type;

  return (
    <>
      <Button data-testid="ai-assist-launcher" type="button" variant="outline"
        disabled={!available} onClick={launch}
        className="w-full rounded-lg h-11 border-violet-200 text-violet-700 hover:bg-violet-50 mt-2">
        <Sparkles className="w-4 h-4 mr-1.5" />
        {available ? "Ask AI" : paused ? "AI is paused — ask your admin" : "AI is off — ask your admin"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ai-workbench" className="max-w-[96vw] w-[96vw] h-kb max-h-kb p-0 gap-0 overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">AI Identify workbench</DialogTitle>
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100 shrink-0">
            <Sparkles className="w-5 h-5 text-violet-600" />
            <span className="font-display font-bold text-slate-800">AI Identify</span>
            <AiSuggestedBadge />
            <span className="text-[11px] text-slate-400 hidden md:inline">Reads only the photos you pick + on-screen context — nothing else.</span>
            {/* THE CREW GUIDE IS ONE TAP FROM THE PANEL ITSELF — nobody hunts a menu for it. */}
            <a data-testid="ai-crew-guide-link" href="/ask-ai-guide" target="_blank" rel="noreferrer"
              className={`ml-auto inline-flex items-center ${CHOICE_CLASS} border-violet-300 text-violet-800 hover:border-violet-600 hover:bg-violet-50`}>
              <span className="hidden sm:inline">How to use this (crew guide)</span><span className="sm:hidden">Crew guide</span>
            </a>
            <Button data-testid="ai-export-findings" type="button" size="sm" variant="outline" disabled={!result && !numbers} onClick={exportFindings} className="rounded-lg h-8 text-xs">
              <Download className="w-3.5 h-3.5 mr-1" /><span className="hidden sm:inline">Export AI findings</span><span className="sm:hidden">Export</span>
            </Button>
            <button data-testid="ai-workbench-close" onClick={() => setOpen(false)} className="shrink-0 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
            {/* LEFT — photo stage + picker */}
            <div className="lg:w-3/5 bg-slate-900 flex flex-col min-h-0">
              {list.length === 0 ? (
                <div data-testid="ai-workbench-no-photos" className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <ImageOff className="w-8 h-8" /><span className="text-sm">No photos on this part yet.</span>
                </div>
              ) : (
                <>
                  <div data-testid="ai-photo-main" className="relative flex-1 min-h-0 overflow-hidden">
                    {/* THE SAME PHOTO SURFACE AS EVERY OTHER SCREEN — zoom, rotate, drag, pinch —
                        and "Full" opens the one viewer, so the Ask-AI panel no longer carries its
                        own copy of any of it (his instruction, 2026-10-15). */}
                    <PhotoStage fileId={list[active].file_id} alt={`photo ${active + 1}`} gestures={photoG}
                      controls={false} testidPrefix="ai-photo" className="p-3" />
                    <div className="absolute top-4 right-4 flex gap-1.5 items-center">
                      <PhotoControls g={photoG} testidPrefix="ai-photo" />
                      <button data-testid="ai-photo-full" onClick={(e) => { e.stopPropagation(); setFullIdx(active); }}
                        title="Full screen" className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/90 text-slate-700 hover:bg-white shadow border border-slate-200/60">
                        <Maximize2 className="w-5 h-5" />
                      </button>
                    </div>
                    <button data-testid="ai-photo-toggle-select" onClick={(e) => { e.stopPropagation(); toggleSelect(list[active].file_id); }}
                      className={`absolute top-4 left-4 rounded-full px-3 py-1.5 text-xs font-semibold ${selected.has(list[active].file_id) ? "bg-violet-600 text-white" : "bg-black/50 text-white hover:bg-black/70"}`}>
                      {selected.has(list[active].file_id) ? <><Check className="w-3.5 h-3.5 mr-1 inline" />Sending</> : "Tap to send"}
                    </button>
                    {list.length > 1 && (
                      <>
                        <button data-testid="ai-photo-prev" onClick={(e) => { e.stopPropagation(); stepPhoto(-1); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"><ChevronLeft className="w-5 h-5" /></button>
                        <button data-testid="ai-photo-next" onClick={(e) => { e.stopPropagation(); stepPhoto(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"><ChevronRight className="w-5 h-5" /></button>
                      </>
                    )}
                    <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] text-white/80 bg-black/50 rounded-full px-2 py-0.5">{active + 1} / {list.length} · <span data-testid="ai-photo-selected-count">{selected.size} selected</span>{selected.size > PHOTO_DEFAULT && photoReason ? ` · ${PHOTO_REASONS.find((r) => r.key === photoReason)?.label || photoReason}` : ""} · {photoG.zoomed ? "drag to move" : "tap or pinch to zoom"}</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto p-2 border-t border-white/10 shrink-0">
                    {list.map((p, i) => {
                      const on = selected.has(p.file_id);
                      return (
                        <button key={p.file_id} data-testid={`ai-photo-thumb-${i}`} onClick={() => { setActive(i); photoG.reset(); toggleSelect(p.file_id); }}
                          className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${on ? "border-violet-400" : "border-transparent opacity-60 hover:opacity-100"}`}>
                          <AuthImg fileId={p.file_id} thumb className="w-full h-full object-cover" alt={`thumb ${i + 1}`} />
                          {on && <span className="absolute inset-0 bg-violet-500/20 flex items-start justify-end p-0.5"><Check className="w-3.5 h-3.5 text-white bg-violet-600 rounded-full p-0.5" /></span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* RIGHT — grouped inline review */}
            <div className="lg:w-2/5 flex flex-col min-h-0 border-t lg:border-t-0 lg:border-l border-slate-100">
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* TRANSPARENCY — exactly what preset info is handed to the AI from this
                    part (VIN + every populated field on the record, plus your live edits).
                    Staff SEE what they're giving it: presetting a field = telling the AI
                    "here's what I know." */}
                {(() => {
                  const liveVals = { ...(context || {}), tag_serial: tagSerial, existing_numbers: numbersOnFile.join(", ") };
                  const LIVE_LABELS = { year: "year", make: "make", model: "model", engine: "engine", category: "category on file", tag_serial: "tag/serial on file", existing_numbers: "numbers already recorded on this part" };
                  const sentMap = new Map((sentPreview || []).map((s) => [s.key, { label: s.label, value: s.value }]));
                  Object.entries(liveVals).forEach(([k, v]) => {
                    if (v && String(v).trim()) sentMap.set(k, { label: sentMap.get(k)?.label || LIVE_LABELS[k] || k, value: String(v).trim() });
                  });
                  const sentList = [...sentMap.entries()].map(([key, o]) => ({ key, ...o }));
                  return (
                    <div data-testid="ai-sent-panel" className="rounded-xl border border-slate-200 bg-slate-50/70 p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="w-3 h-3 text-violet-600" />Sent to the AI from this part
                      </div>
                      {sentList.length === 0 ? (
                        <p className="text-[11px] text-slate-400">Nothing preset yet — the AI will work from the photos alone. Fill in fields (VIN comes from the truck) to hand it more.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {sentList.map((s) => (
                            <Badge key={s.key} data-testid={`ai-ctx-${s.key}`} className="rounded-full bg-white hover:bg-white text-slate-600 border border-slate-200 text-[11px] font-mono">
                              <span className="text-slate-400 mr-1">{s.label}:</span>{s.value}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1.5">Reasoning hints only — the AI never turns a VIN, note, or hint into a part number.</p>
                    </div>
                  );
                })()}

                {/* STAFF HINT — feed what you know back into identification (same model,
                    steered by your knowledge). Read-only reasoning; never becomes a number. */}
                <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-2.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-violet-700 flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3 h-3" />Add what you know — steer the AI
                  </label>
                  <Input data-testid="ai-hint-input" value={hint} onChange={(e) => setHint(e.target.value)}
                    placeholder="e.g. it's an oil cooler off a DD15"
                    className="h-9 text-sm bg-white border-violet-200 focus-visible:ring-violet-400" />
                  <p className="text-[10px] text-slate-400 mt-1">Optional. The AI weights this heavily — it never invents a part number from it.</p>
                </div>

                {/* PICK UP WHERE IT STOPPED — his instruction, 2026-10-14, after a 30-photo read
                    died partway through: finished batches are marked done, a re-run skips them by
                    default, and "re-read everything" is one deliberate tick. */}
                {list.length > 0 && readState?.tasks && (
                  <ResumeStrip readState={readState} list={list} selected={selected}
                    setSelected={setSelected} rereadAll={rereadAll} setRereadAll={setRereadAll}
                    photoReason={photoReason} setWhyOpen={setWhyOpen} />
                )}
                {list.length > 0 && selected.size === 0 && (
                  <div data-testid="ai-pick-photos-nudge" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
                    <Camera className="w-4 h-4 mt-0.5 shrink-0" />
                    <div><b>Pick the photos to send.</b> Tap a photo (or its thumbnail) to choose what the AI looks at — nothing is sent until you select it.</div>
                  </div>
                )}
                {bgRead && (
                  <div data-testid="ai-big-read-progress" className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
                    <div className="flex items-center gap-2 font-semibold">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Reading {bgRead.total} photos in batches on the server
                    </div>
                    <div className="mt-1 text-[12px]">{bgRead.message || "Starting…"}</div>
                    <div className="mt-2 h-2 rounded-full bg-violet-100 overflow-hidden">
                      <div className="h-full bg-violet-500 transition-all"
                        style={{ width: `${Math.round(((bgRead.read || 0) / Math.max(1, bgRead.total || 1)) * 100)}%` }} />
                    </div>
                    <div className="mt-1.5 text-[11px] text-violet-700">You can close this and come back — the read keeps going and the answer will be here.</div>
                  </div>
                )}
                {!result && (
                  <HelpTip className="w-full" testid="ai-help-identify"
                    text="One charged read. Looks at the SHAPE of the part in the photos you ticked and suggests a name, brand, type, category and search words. Nothing is applied until you click it.">
                  <Button data-testid="ai-identify-btn" type="button" disabled={busy || !selected.size} onClick={runIdentify}
                    className="w-full rounded-xl h-14 bg-violet-600 hover:bg-violet-700 text-white text-base font-semibold">
                    {busy ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Reading your photos…</> : <><Sparkles className="w-5 h-5 mr-2" />{hint.trim() ? "Identify with your hint" : "Identify this part"}</>}
                  </Button>
                  </HelpTip>
                )}
                {!result && !busy && <p className="text-[11px] text-slate-400">Sends {selected.size} selected photo(s) + the context above. One call fills every field it can — each shown here with a Use.{selected.size > PHOTO_DEFAULT ? ` Read in batches of 5${selected.size > BACKGROUND_ABOVE ? " as a background job you can walk away from" : ""}.` : ""}</p>}

                {result && (
                  <>
                    {restored && (
                      <div data-testid="ai-restored-note" className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-500 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-violet-400" />Showing your last AI run{result.at ? ` · ${(result.at).replace("T", " ").slice(0, 16)}` : ""}. Re-run to refresh.
                      </div>
                    )}
                    {abstained && (
                      <div data-testid="ai-abstain-notice" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div><b>AI couldn't identify it confidently.</b><div className="text-[13px] mt-0.5">{result.abstain_reason || "Check the photo — try a closer, straight-on shot."}</div></div>
                      </div>
                    )}

                    {/* IDENTITY */}
                    {hasIdentity && (
                      <>
                        <GroupTitle>Identity</GroupTitle>
                        <FieldRow field="name" label="Name" value={d.suggested_name} level={conf.name} />
                        <FieldRow field="brand" label="Brand" value={d.suggested_brand} level={conf.brand} />
                        <FieldRow field="category" label="Category" value={d.suggested_category} level={conf.category} />
                        <FieldRow field="part_type" label="Part type" value={d.suggested_part_type} level={conf.part_type} />
                      </>
                    )}

                    {/* DESCRIPTION */}
                    {d.description && (
                      <>
                        <GroupTitle>Description</GroupTitle>
                        <div className="rounded-xl border border-slate-200 bg-white p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5"><span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Listing description</span>{confChip(conf.description)}</div>
                            <Button data-testid="ai-use-description" type="button" size="sm" disabled={applied.has("description")}
                              onClick={() => use("description", d.description, "Description")}
                              className="rounded-lg h-8 bg-[#E0781A] hover:bg-[#C76714] text-white text-xs shrink-0">
                              {applied.has("description") ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Used</> : <><PlusCircle className="w-3.5 h-3.5 mr-1" />Use</>}
                            </Button>
                            {applied.has("description") && (
                              <ChoiceButton tone="danger" data-testid="ai-undo-description" onClick={() => undoField("description", "Description")}>
                                <Undo2 className="w-3 h-3 mr-1" />Take it back out
                              </ChoiceButton>
                            )}
                          </div>
                          <p data-testid="ai-describe-desc" className="text-sm text-slate-700">{d.description}</p>
                        </div>
                      </>
                    )}

                    {/* KEYWORDS (free) */}
                    {(d.suggested_keywords || []).length > 0 && (
                      <>
                        <GroupTitle action={<Button data-testid="ai-add-all-keywords" type="button" size="sm" variant="outline" onClick={() => addKw(d.suggested_keywords, "__all__")} className="rounded-lg h-7 text-xs"><PlusCircle className="w-3.5 h-3.5 mr-1" />Add all</Button>}>Keywords (free)</GroupTitle>
                        <div className="flex flex-wrap gap-1.5">
                          {d.suggested_keywords.map((kw, i) => (
                            <button key={i} data-testid={`ai-keyword-${i}`} type="button"
                              title={addedKw.has(kw) ? "Click again to take this word back out" : "Add this search word"}
                              onClick={() => (addedKw.has(kw) ? undoKeyword(kw, kw) : addKw(kw, kw))}
                              className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2.5 py-1 border ${addedKw.has(kw) ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
                              {addedKw.has(kw) ? <Check className="w-3 h-3" /> : <PlusCircle className="w-3 h-3" />}{kw}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {/* VALUE — Tier 1 ballpark (this same call) + Tier 2 live market (Perplexity) */}
                    <GroupTitle>Value</GroupTitle>
                    {(d.ballpark_value_usd != null || d.ballpark_low_usd != null || d.ballpark_high_usd != null || d.ballpark_avg_usd != null) ? (
                      <div data-testid="ai-ballpark-value" className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Rough internal ballpark</span>
                          {confChip(conf.value)}
                          <Button data-testid="ai-use-ballpark" type="button" size="sm"
                            disabled={applied.has("value") || (d.ballpark_value_usd ?? d.ballpark_avg_usd) == null}
                            onClick={() => use("value", d.ballpark_value_usd ?? d.ballpark_avg_usd, "Ballpark value")}
                            className="ml-auto rounded-lg h-8 text-xs bg-[#E0781A] hover:bg-[#C76714] text-white">
                            {applied.has("value") ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Used</> : <><PlusCircle className="w-3.5 h-3.5 mr-1" />Use recommended</>}
                          </Button>
                          {applied.has("value") && (
                            <ChoiceButton tone="danger" data-testid="ai-undo-value" onClick={() => undoField("value", "Ballpark value")}>
                              <Undo2 className="w-3 h-3 mr-1" />Take it back out
                            </ChoiceButton>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          {[["Low", d.ballpark_low_usd, false], ["Average", d.ballpark_avg_usd, false], ["High", d.ballpark_high_usd, false], ["Recommended", d.ballpark_value_usd, true]].map(([lbl, v, hi]) => (
                            <button key={lbl} type="button" data-testid={`ai-ballpark-${String(lbl).toLowerCase()}`} disabled={v == null || applied.has("value")}
                              onClick={() => use("value", v, `${lbl} value`)} title={v == null ? "" : `Use $${Number(v).toLocaleString()} as the internal value`}
                              className={`rounded-lg px-2 py-1.5 text-center transition-colors disabled:cursor-default ${hi ? "bg-[#E0781A] text-white hover:bg-[#C76714]" : "bg-white border border-amber-200 text-slate-700 hover:border-[#E0781A] hover:bg-amber-100"} ${v == null ? "opacity-50" : ""}`}>
                              <div className={`text-[9px] font-bold uppercase tracking-wider ${hi ? "text-white/80" : "text-slate-400"}`}>{lbl}</div>
                              <div className="text-sm font-display font-bold">{v == null ? "—" : `$${Number(v).toLocaleString()}`}</div>
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] text-amber-800/80 mt-1.5">Training-based guess, <b>NOT live market data</b>. {d.ballpark_note ? `${d.ballpark_note}. ` : ""}Tap any figure (or “Use recommended”) to drop it into Value as <b>“AI Assist — unverified · ESTIMATE”</b> — you accept or override; never auto-set.</p>
                      </div>
                    ) : (
                      <div data-testid="ai-ballpark-none" className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3 text-[13px] text-slate-500">No ballpark estimate from this photo — set the internal value manually.</div>
                    )}

                    {!marketReady ? (
                      <div data-testid="ai-market-dormant" className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 flex items-center gap-2 mt-2">
                        <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-[13px] text-slate-500 flex-1"><b>Market pricing not enabled.</b> Add a research key to look up live market comps.</span>
                        <Button type="button" size="sm" variant="outline" disabled className="rounded-lg h-8 text-xs opacity-60">Current Market Value</Button>
                      </div>
                    ) : !priceResult ? (
                      <div className="mt-2">
                        <Button data-testid="ai-market-btn" type="button" size="sm" variant="outline" disabled={priceBusy} onClick={runMarket}
                          className="rounded-lg h-9 text-xs border-sky-200 text-sky-700 hover:bg-sky-50">
                          {priceBusy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Searching live market…</> : <><Globe className="w-3.5 h-3.5 mr-1" />Current Market Value (live web)</>}
                        </Button>
                      </div>
                    ) : (
                      <div data-testid="ai-market-result" className="rounded-xl border border-sky-200 bg-sky-50 p-3 mt-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-sky-100 rounded-full px-2 py-0.5">Live market</span>
                          {priceResult.pricing?.range?.median != null && <span className="text-lg font-display font-bold text-slate-800">${Number(priceResult.pricing.range.median).toLocaleString()}</span>}
                          {priceResult.pricing?.range?.median != null && (
                            <Button data-testid="ai-use-market" type="button" size="sm" disabled={applied.has("market_value")}
                              onClick={async () => {
                                if (onMarketApply) {
                                  const ok = await onMarketApply(priceResult.return_id);
                                  if (ok) setApplied((s) => new Set(s).add("market_value"));
                                } else {
                                  use("market_value", Math.round(Number(priceResult.pricing.range.median)), "Market value");
                                }
                              }}
                              className="ml-auto rounded-lg h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                              {applied.has("market_value") ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Applied</> : <><PlusCircle className="w-3.5 h-3.5 mr-1" />Use as Market (Perplexity)</>}
                            </Button>
                          )}
                          {applied.has("market_value") && (
                            <ChoiceButton tone="danger" data-testid="ai-undo-market" onClick={() => undoField("market_value", "Market value")}>
                              <Undo2 className="w-3 h-3 mr-1" />Take it back out
                            </ChoiceButton>
                          )}
                        </div>
                        {priceResult.pricing?.range && <p className="text-[11px] text-slate-500 mt-1">Range ${priceResult.pricing.range.min ?? "?"}–${priceResult.pricing.range.max ?? "?"} · n={priceResult.pricing.range.n ?? 0}. Applying stamps the value <b>“Market (Perplexity)”</b> with its citations.</p>}
                        {priceResult.pricing?.summary && <p className="text-[12px] text-slate-600 mt-1">{priceResult.pricing.summary}</p>}
                        {(priceResult.pricing?.comps || []).slice(0, 4).map((c, i) => (
                          <a key={i} data-testid={`ai-comp-${i}`} href={c.url} target="_blank" rel="noreferrer" className="block text-[11px] text-sky-700 hover:underline mt-1 truncate">${c.price} — {c.source} {c.sold ? "(sold)" : ""}{c.note ? ` · ${c.note}` : ""}</a>
                        ))}
                        {priceResult.abstained && <p className="text-[11px] text-slate-400 mt-1">{priceResult.abstain_reason || "No solid comps found."}</p>}
                      </div>
                    )}

                    <div className="rounded-lg bg-violet-50 border border-violet-100 p-2.5 text-[11px] text-violet-800">
                      Used descriptive fields land as <b>AI Assist — unverified</b>. Confirm each with <b>Verify</b> on the form. Keywords save free. Finalize stays blocked until verified.
                    </div>

                    <Button data-testid="ai-identify-again" type="button" variant="ghost" size="sm" disabled={busy} onClick={runIdentify} className="text-xs text-slate-500">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (hint.trim() ? "Re-identify with your hint" : "Re-run identify")}
                    </Button>
                  </>
                )}

                {/* NUMBERS — OCR only; flexible destination */}
                <div className="pt-2 border-t border-slate-100">
                  <GroupTitle>Numbers on the part</GroupTitle>
                  {!numbers ? (
                    <HelpTip className="w-full mt-1.5" testid="ai-help-read-numbers"
                      text="One charged read, numbers only. Reads the characters stamped or printed on the tags in the photos you ticked. If a read already stopped partway, it picks up where it stopped — the photos already read are skipped.">
                    <Button data-testid="ai-read-numbers-btn" type="button" variant="outline" disabled={numBusy || !selected.size} onClick={() => runNumbers(false)} className="w-full rounded-lg h-10 text-sm">
                      {numBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Reading tag…</> : <><ScanText className="w-4 h-4 mr-1.5" />Read numbers off the part</>}
                    </Button>
                    </HelpTip>
                  ) : (
                    <div className="space-y-2 mt-1.5">
                      {numbers.abstained && <div className="text-[13px] text-amber-700">Couldn't read a number clearly — check the photo / try a closer tag shot.</div>}
                      {renderNumbers()}
                      {!numbers.abstained && (numbers.numbers || []).length === 0 && <div className="text-[13px] text-slate-500">No readable numbers found.</div>}

                      {(d.observed_markings || []).length > 0 && (
                        <div data-testid="ai-related-markings" className="rounded-xl border border-slate-200 bg-white p-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Other markings / related (reference — Copy only)</div>
                          <div className="flex flex-wrap gap-1.5">
                            {d.observed_markings.map((m, i) => (
                              <button key={i} data-testid={`ai-marking-${i}`} type="button" onClick={() => copyText(m)} className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-mono px-2 py-1 hover:bg-slate-100"><Copy className="w-3 h-3" />{m}</button>
                            ))}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-1.5">Related / interchange numbers are never inserted into a fitment field.</div>
                        </div>
                      )}

                      {(numbers.own_tags || []).length > 0 && (
                        <div data-testid="ai-own-tags" className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
                          <b>Our own tag was in the photo:</b> <span className="font-mono">{numbers.own_tags.join(", ")}</span> — left out of the list. That's our filing code, not a number on the part.
                        </div>
                      )}

                      {/* RE-RUN READ NUMBERS ON ITS OWN — his instruction, 2026-10-14: numbers
                          only, no re-identify, its own read, groups rebuilt. It SUGGESTS: nothing
                          you have already added or verified on the part is touched. */}
                      <HelpTip testid="ai-help-read-again"
                        text="Reads the NUMBERS again on the photos you have ticked — a fresh charged read. It never re-identifies the part, and nothing already added or verified on the part is touched: the answer only suggests.">
                      <Button data-testid="ai-read-numbers-again" type="button" variant="ghost" size="sm"
                        disabled={numBusy || !selected.size} onClick={() => runNumbers(true)} className="text-xs text-slate-500">
                        {numBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Read the numbers again (${selected.size} photo${selected.size === 1 ? "" : "s"}, charged)`}
                      </Button>
                      </HelpTip>
                      <div className="rounded-lg bg-amber-50 border border-amber-100 p-2.5 text-[11px] text-amber-800">
                        <b>Insert saves typing — it does NOT verify.</b> An inserted OEM/serial lands <b>unverified</b> and editable; correct any misread, then click <b>✓verify</b> to confirm. Finalize stays blocked until you do. Keyword adds are free. Interchange/related = Copy only.
                      </div>
                    </div>
                  )}
                </div>

                {isAdmin && result?.cost && (
                  <div data-testid="ai-cost-inline" className="text-[11px] font-mono text-slate-400">
                    call cost: ${result.cost.actual_usd?.toFixed(5)} · {result.cost.input_tokens}+{result.cost.output_tokens} tok
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clear finish: Done completes the AI session and returns to the part. */}
          <div className="shrink-0 border-t border-slate-100 px-4 py-3 flex items-center justify-between gap-3 bg-white">
            <span className="text-[11px] text-slate-400 hidden sm:inline">Your suggestions are saved on the part. Come back to Ask AI anytime.</span>
            <Button data-testid="ai-workbench-done" type="button" onClick={() => setOpen(false)}
              className="rounded-lg h-11 px-6 bg-[#E0781A] hover:bg-[#C76714] text-white font-semibold ml-auto">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MORE THAN 4 PHOTOS — WHY, with quick choices, so it is deliberate and on the record. */}

      {renameFor !== null && numbers?.return_id && (
        <TagHeadingRename open={renameFor !== null} onOpenChange={(v) => { if (!v) setRenameFor(null); }}
          partId={partId} returnId={numbers.return_id} gi={renameFor}
          current={groupName(tagGroups[renameFor], renameFor)} was={renamedTo(renameFor)?.was || null}
          aiWords={(tagGroups[renameFor]?.name || "").trim() || `Tag ${renameFor + 1}`}
          canApproveNames={has("manage_lists")} brand={tagInfo(renameFor)?.brand || tagInfo(renameFor)?.suggested?.brand || ""}
          onRenamed={(data) => onRenamed(renameFor, data)} />
      )}
      {/* ONE OF EACH, NEVER TWO SILENTLY (his instruction, 2026-10-17). The notice names the number
          that is already there and the one being offered, and nothing moves until he picks. */}
      <Dialog open={!!replaceAsk} onOpenChange={(v) => { if (!v) setReplaceAsk(null); }}>
        <DialogContent data-testid="ai-replace-ask" className="max-w-md rounded-xl">
          <DialogTitle className="font-display font-bold text-slate-800">
            {replaceAsk?.owner} already has {replaceAsk?.what === "serial number" ? "Serial #" : "OEM #"} {replaceAsk?.existing} — replace it, or keep it?
          </DialogTitle>
          <div className="text-sm text-slate-600 space-y-2">
            <div>
              <span className="text-slate-500">Already there:</span>{" "}
              <b data-testid="ai-replace-existing" className="font-mono text-slate-900">{replaceAsk?.existing}</b>
            </div>
            <div>
              <span className="text-slate-500">You are adding:</span>{" "}
              <b data-testid="ai-replace-next" className="font-mono text-slate-900">{replaceAsk?.next}</b>
            </div>
            <div className="text-[12px] text-slate-500">
              A {replaceAsk?.what} is kept one at a time. Replacing records the change; keeping leaves it exactly as it is.
            </div>
            {replaceAsk?.owner !== "The assembly" && (
              <div data-testid="ai-replace-old-stays" className="text-[12px] text-slate-500">
                {replaceAsk?.existing} stays on the part as {replaceAsk?.what === "serial number" ? "a number printed on it" : "a spare number"}.
                Taking {replaceAsk?.next} back out later does not put {replaceAsk?.existing} back.
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid="ai-replace-confirm" type="button"
              onClick={() => {
                const r = replaceAsk; setReplaceAsk(null);
                // the number it replaces stops claiming the spot, so two rows never both read DONE
                if (r?.existing) setPlacedAs((s2) => {
                  const next = { ...s2 };
                  Object.keys(next).forEach((k) => { if (sameVal(k, r.existing)) delete next[k]; });
                  return next;
                });
                r?.run?.();
              }}
              className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white text-sm">
              Replace with {replaceAsk?.next}
            </Button>
            <Button data-testid="ai-replace-keep" type="button" variant="outline"
              onClick={() => setReplaceAsk(null)} className="rounded-lg text-sm">
              Keep {replaceAsk?.existing}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={whyOpen} onOpenChange={(v) => { setWhyOpen(v); if (!v) setPendingPhoto(null); }}>
        <DialogContent data-testid="ai-photo-why" className="max-w-md rounded-xl">
          <DialogTitle className="font-display font-bold text-slate-800">More than {PHOTO_DEFAULT} photos — why?</DialogTitle>
          <p className="text-[13px] text-slate-500 -mt-1">
            There is no limit. This is recorded with your name, the part and how many photos — it is
            how the SOP gets written.
          </p>
          <div className="space-y-2">
            {PHOTO_REASONS.map((r) => (
              <Button key={r.key} data-testid={`ai-photo-why-${r.key}`} type="button" variant="outline"
                onClick={() => confirmWhy(r.key)}
                className="w-full justify-start rounded-lg h-12 text-sm border-slate-300">
                {r.label}
              </Button>
            ))}
            <Input data-testid="ai-photo-why-note" value={photoNote} onChange={(e) => setPhotoNote(e.target.value)}
              placeholder="Note (required for Other)" className="h-10 text-sm rounded-lg" />
          </div>
        </DialogContent>
      </Dialog>

      {/* FULL SCREEN, FROM THE PANEL — the one viewer, same as every other screen. */}
      <PhotoLightbox photos={list} index={fullIdx} onIndex={setFullIdx} onClose={() => setFullIdx(null)}
        testidPrefix="ai-photo-full" fixScope="part" fixRecordId={partId} />

      {/* RED TWIN VIEW — opened from the red line on a row. "Use the held number" puts it in that
          row's own edit box, so the crew member adds it through the normal buttons and still
          ✓verifies it. Nothing is applied by looking. */}
      <RedTwinView partId={partId} value={twinFor?.value} open={!!twinFor}
        onOpenChange={(v) => !v && setTwinFor(null)}
        onUseTwin={(held) => {
          const i = twinFor?.row;
          if (i === undefined || i === null) return;
          setNumEdits((s) => ({ ...s, [i]: held }));
          setEditNum((s) => new Set(s).add(i));
          toast.success(`${held} put in — check it against the stamp, then add it`);
        }} />
    </>
  );
}
